import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import html2canvas from "html2canvas";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  fetchImportCustomerNamesForIncentive,
  fetchPartyDeductionsForIncentive,
} from "../services/monthlyIncentivePartySalesService";
import { fetchSpecialGroupIncentiveBundle } from "../services/employeeSpecialGroupIncentiveService";
import { EMPLOYEE_SPECIAL_GROUP_INCENTIVE_PLANS } from "../config/employeeSpecialIncentivePlans";
import {
  EMPLOYEE_STAFF_POOL_PCT_PLANS,
  computeStaffPoolPctIncentive,
  getStaffPoolPctPlanForPerson,
} from "../config/employeeStaffPoolPctPlans";
import { ATTENDANCE_BRANCHES } from "../config/attendanceBranches";
import { db } from "../firebase";
import { MONTHLY_AUTOS_BRANDS } from "../services/monthlyAutosReportService";
import { loadMonthSalePageData } from "../services/yearlySaleReportService";
import { resolveCollectionName } from "../services/attendanceCollections";
import {
  saveAccountantPhone as saveAccountantPhoneCloud,
  saveIncentiveConfig as saveIncentiveConfigCloud,
  subscribeIncentiveConfig,
} from "../services/monthlyIncentiveConfigService";
import {
  loadMonthlyIncentiveSnapshot,
  saveMonthlyIncentiveSnapshot,
} from "../services/monthlyIncentiveSnapshotService";
import { monthlySaleReportQuery } from "../utils/monthlySaleReportNav";
import {
  formatFinancialYearLabel,
  getCalendarMonthFromFySlot,
  getFinancialYearDropdownOptions,
  getFinancialYearMonthSequence,
  getFYSlotForCalendarDate,
  readStoredFinancialYearStartYear,
} from "../utils/financialYear";
import { getVisibleAppBranches } from "../config/appBranches";
import {
  computeOeIncentive,
  computeOrdinaryIncentive,
  DEFAULT_MISC_DIVISOR,
  DEFAULT_OE_RATE_PCT,
  DEFAULT_ORD_RATE_PCT,
  brandSaleRemainder,
  grossOeSaleForBrand,
  grossOrdinarySaleForBrand,
  incentiveOrdinaryMiscSharePerBrand,
  oeOrdSaleReturnAmounts,
} from "../helpers/monthlyIncentiveCalc";

const LS_CONFIG = "monthlyIncentiveConfig_v3";
const LS_ACCOUNTANT_PHONE = "monthlyIncentiveAccountantPhone_v1";

/** Leave deduction: (gross incentive for that line ÷ this) × leave days. */
const LEAVE_DAYS_IN_MONTH = 30;

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const BRANCH_OPTIONS = [
  { value: "Main", label: "Main Branch" },
  { value: "Branch 2", label: "Spareway" },
  { value: "Branch 3", label: "Branch" },
];

/** Billing branch → same employee Firestore group as payroll / holidays. */
const BILLING_BRANCH_TO_ATTENDANCE_INDEX = { Main: 0, "Branch 2": 1, "Branch 3": 2 };

/** Brands with OE / ordinary columns on the monthly autos report (excludes MAHINDRA). */
const INCENTIVE_BRANDS = MONTHLY_AUTOS_BRANDS.filter((b) => b !== "MAHINDRA");
const SPAREWAY_BRANCH_VALUE = "Branch 2";
const SPAREWAY_OE_MIN_SALE = 2700000;
const SPAREWAY_OE_RATE_PCT = 0.5;
const SPAREWAY_ORD_MIN_SALE = 1000000;
const SPAREWAY_ORD_RATE_PCT = 1;

function parseNum(v) {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function fmt(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

/** Sum of % of section across all brand lines for one employee. */
function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (n <= 0) return "—";
  return `${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)}%`;
}

/** One line: employee sells one brand at a % of that brand’s section; optional manual cut gets pooled. */
function defaultStaffRow() {
  return {
    employeeId: "",
    name: "",
    brandId: "",
    pct: "",
    leave: "",
    cashBranding: true,
    autoLeaveCutEnabled: true,
    leaveCashCutEnabled: false,
    leaveCashCutAmt: "",
  };
}

/** Resolve display name from Firestore doc (same field order as HolidaysPage). */
function employeeRecordFromDoc(docSnap) {
  const d = docSnap.data() || {};
  const employeeId = String(
    d.employeeId ?? d.empId ?? d.user_id ?? d.userId ?? d.EmployeeId ?? docSnap.id ?? ""
  ).trim();
  const id = employeeId || docSnap.id;
  const employeeName = String(
    d.EmployeeName ?? d.employeeName ?? d.fullName ?? d.name ?? d.Name ?? id
  ).trim();
  const phone = String(d.phone ?? d.mobile ?? d.Mobile ?? "").trim();
  return { employeeId: id, employeeName: employeeName || id, phone };
}

/** Digits only; default India 91 for 10-digit local numbers (WhatsApp wa.me format, no +). */
function sanitizePhoneForWhatsApp(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("0")) d = d.replace(/^0+/, "") || "";
  if (d.length === 10) return `91${d}`;
  return d;
}

function whatsAppSendUrl(phoneDigits, message) {
  const text = encodeURIComponent(message);
  if (phoneDigits && phoneDigits.length >= 10) {
    return `https://wa.me/${phoneDigits}?text=${text}`;
  }
  return `https://wa.me/?text=${text}`;
}

function isCashBrandingRow(s) {
  if (s == null) return true;
  if (s.cashBranding === false) return false;
  if (s.cashBranding === "false" || s.cashBranding === "0") return false;
  return true;
}

function isLeaveCashCutEnabledRow(s) {
  if (s == null) return false;
  if (s.leaveCashCutEnabled === true) return true;
  if (s.leaveCashCutEnabled === "true" || s.leaveCashCutEnabled === "1") return true;
  return false;
}

function isAutoLeaveCutEnabledRow(s) {
  if (s == null) return true;
  if (s.autoLeaveCutEnabled === false) return false;
  if (s.autoLeaveCutEnabled === "false" || s.autoLeaveCutEnabled === "0") return false;
  return true;
}

function leaveCashCutAmtRow(s) {
  const n = parseNum(s?.leaveCashCutAmt);
  return Math.max(0, n);
}

/** Spareway per-brand manual OE / ord sale — blank means use import figure. */
function sparewayBrandSaleValue(config, brandId, field, computed) {
  const raw = config.sparewayBrandSales?.[brandId]?.[field];
  return String(raw ?? "").trim() !== "" ? parseNum(raw) : computed;
}

function sparewayBrandSaleIsManual(config, brandId, field) {
  return String(config.sparewayBrandSales?.[brandId]?.[field] ?? "").trim() !== "";
}

/** Split `total` across items by weight; last gets remainder so sum matches. */
function splitByWeights(total, items) {
  const out = new Map();
  if (!items.length || total === 0) return out;
  const wsum = items.reduce((a, x) => a + Math.max(0, x.weight), 0);
  if (wsum <= 0) {
    const n = items.length;
    const each = total / n;
    let acc = 0;
    items.forEach((x, i) => {
      const v = i === n - 1 ? total - acc : each;
      acc += v;
      out.set(x.key, v);
    });
    return out;
  }
  let acc = 0;
  items.forEach((x, i) => {
    if (i === items.length - 1) out.set(x.key, total - acc);
    else {
      const v = (total * Math.max(0, x.weight)) / wsum;
      acc += v;
      out.set(x.key, v);
    }
  });
  return out;
}

function normalizeDeductParties(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((p) => ({
      partyName: String(p?.partyName ?? p?.name ?? "").trim(),
      id: String(p?.id ?? p?.partyId ?? "").trim(),
    }))
    .filter((p) => p.partyName);
}

function defaultConfig() {
  return {
    brandCuts: {},
    mins: {
      HERO: { oeMin: "1000000", ordMin: "500000" },
    },
    miscDivisor: String(DEFAULT_MISC_DIVISOR),
    oeRatePct: String(DEFAULT_OE_RATE_PCT),
    ordRatePct: String(DEFAULT_ORD_RATE_PCT),
    sparewayBrandSales: {},
    sparewayBrTransferOe: "",
    sparewayBrTransferOrd: "",
    sparewayOeMinSale: String(SPAREWAY_OE_MIN_SALE),
    sparewayOrdMinSale: String(SPAREWAY_ORD_MIN_SALE),
    staff: [defaultStaffRow()],
    deductParties: [],
  };
}

/** Flatten legacy shapes into { name, brandId, pct, leave }[]. */
function normalizeStaffList(staff) {
  const def = [defaultStaffRow()];
  if (!Array.isArray(staff) || !staff.length) return def;
  const first = staff[0];

  if (first?.byBrand && typeof first.byBrand === "object") {
    const out = [];
    for (const s of staff) {
      const name = s?.name != null ? String(s.name) : "";
      const lb = s.leaveByBrand && typeof s.leaveByBrand === "object" ? s.leaveByBrand : {};
      let any = false;
      for (const bid of INCENTIVE_BRANDS) {
        const p = parseNum(s.byBrand?.[bid]);
        if (p > 0) {
          any = true;
          out.push({
            employeeId: s?.employeeId != null ? String(s.employeeId) : "",
            name,
            brandId: bid,
            pct: String(s.byBrand[bid]),
            leave: lb[bid] != null && String(lb[bid]).trim() !== "" ? String(lb[bid]) : "",
            cashBranding: isCashBrandingRow(s),
            autoLeaveCutEnabled: isAutoLeaveCutEnabledRow(s),
            leaveCashCutEnabled: isLeaveCashCutEnabledRow(s),
            leaveCashCutAmt: s?.leaveCashCutAmt != null ? String(s.leaveCashCutAmt) : "",
          });
        }
      }
      if (!any && name.trim())
        out.push({
          employeeId: s?.employeeId != null ? String(s.employeeId) : "",
          name,
          brandId: "",
          pct: "",
          leave: "",
          cashBranding: isCashBrandingRow(s),
          autoLeaveCutEnabled: isAutoLeaveCutEnabledRow(s),
          leaveCashCutEnabled: isLeaveCashCutEnabledRow(s),
          leaveCashCutAmt: s?.leaveCashCutAmt != null ? String(s.leaveCashCutAmt) : "",
        });
    }
    return out.length ? out : def;
  }

  if (first && "sharePct" in first && !("brandId" in first)) {
    return staff.map((s) => ({
      employeeId: s?.employeeId != null ? String(s.employeeId) : "",
      name: s?.name != null ? String(s.name) : "",
      brandId: "",
      pct: "",
      leave: "",
      cashBranding: isCashBrandingRow(s),
      autoLeaveCutEnabled: isAutoLeaveCutEnabledRow(s),
      leaveCashCutEnabled: isLeaveCashCutEnabledRow(s),
      leaveCashCutAmt: s?.leaveCashCutAmt != null ? String(s.leaveCashCutAmt) : "",
    }));
  }

  return staff.map((s) => ({
    employeeId: s?.employeeId != null ? String(s.employeeId) : "",
    name: s?.name != null ? String(s.name) : "",
    brandId: s?.brandId != null ? String(s.brandId) : "",
    pct: s?.pct != null ? String(s.pct) : "",
    leave: s?.leave != null ? String(s.leave) : "",
    cashBranding: isCashBrandingRow(s),
    autoLeaveCutEnabled: isAutoLeaveCutEnabledRow(s),
    leaveCashCutEnabled: isLeaveCashCutEnabledRow(s),
    leaveCashCutAmt: s?.leaveCashCutAmt != null ? String(s.leaveCashCutAmt) : "",
  }));
}

function migrateOlderConfigKeys() {
  try {
    if (localStorage.getItem(LS_CONFIG)) return;
    for (const key of ["monthlyIncentiveConfig_v2", "monthlyIncentiveConfig_v1"]) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const all = JSON.parse(raw);
      if (all && typeof all === "object") {
        const next = {};
        for (const br of Object.keys(all)) {
          const b = all[br];
          if (!b || typeof b !== "object") continue;
          next[br] = { ...b, staff: normalizeStaffList(b.staff) };
        }
        localStorage.setItem(LS_CONFIG, JSON.stringify(next));
        return;
      }
    }
  } catch {
    /* ignore */
  }
}

function normalizeIncentiveConfig(base) {
  const b = base && typeof base === "object" ? base : {};
  const d = defaultConfig();
  return {
    brandCuts: b.brandCuts && typeof b.brandCuts === "object" ? { ...b.brandCuts } : { ...d.brandCuts },
    mins: b.mins && typeof b.mins === "object" ? { ...d.mins, ...b.mins } : { ...d.mins },
    miscDivisor: b.miscDivisor != null ? String(b.miscDivisor) : d.miscDivisor,
    oeRatePct: b.oeRatePct != null ? String(b.oeRatePct) : d.oeRatePct,
    ordRatePct: b.ordRatePct != null ? String(b.ordRatePct) : d.ordRatePct,
    sparewayBrandSales:
      b.sparewayBrandSales && typeof b.sparewayBrandSales === "object"
        ? { ...b.sparewayBrandSales }
        : { ...d.sparewayBrandSales },
    sparewayBrTransferOe: b.sparewayBrTransferOe != null ? String(b.sparewayBrTransferOe) : d.sparewayBrTransferOe,
    sparewayBrTransferOrd: b.sparewayBrTransferOrd != null ? String(b.sparewayBrTransferOrd) : d.sparewayBrTransferOrd,
    sparewayOeMinSale: b.sparewayOeMinSale != null ? String(b.sparewayOeMinSale) : d.sparewayOeMinSale,
    sparewayOrdMinSale: b.sparewayOrdMinSale != null ? String(b.sparewayOrdMinSale) : d.sparewayOrdMinSale,
    staff: normalizeStaffList(b.staff),
    deductParties: normalizeDeductParties(b.deductParties),
  };
}

function loadConfig(branch) {
  migrateOlderConfigKeys();
  try {
    const raw = localStorage.getItem(LS_CONFIG);
    const all = raw ? JSON.parse(raw) : {};
    const base = all[branch] && typeof all[branch] === "object" ? all[branch] : {};
    return normalizeIncentiveConfig(base);
  } catch {
    return defaultConfig();
  }
}

function saveConfig(branch, cfg) {
  try {
    const raw = localStorage.getItem(LS_CONFIG);
    const all = raw ? JSON.parse(raw) : {};
    all[branch] = cfg;
    localStorage.setItem(LS_CONFIG, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

function csvEscape(s) {
  const t = String(s ?? "");
  if (/[",\n\r]/.test(t)) return `"${t.replace(/"/g, '""')}"`;
  return t;
}

export default function MonthlyIncentivePage() {
  const now = new Date();
  const [fyStartYear, setFyStartYear] = useState(() => readStoredFinancialYearStartYear());
  const [fySlot, setFySlot] = useState(() => getFYSlotForCalendarDate(now));
  const [branchFilter, setBranchFilter] = useState("Main");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(() => loadConfig("Main"));
  const configRef = useRef(config);
  const cloudWriteIdRef = useRef("");
  const cloudSaveTimerRef = useRef(null);
  const cloudSaveBusyRef = useRef(false);
  const [cloudStatus, setCloudStatus] = useState({
    state: "loading", // "loading" | "ok" | "error" | "offline"
    error: "",
    savedAt: 0,
  });
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState(null);
  const [accountantPhoneRaw, setAccountantPhoneRaw] = useState("");
  const accountantImageCardRef = useRef(null);
  const [accountantImageBusy, setAccountantImageBusy] = useState(false);
  const [accountantImageUrl, setAccountantImageUrl] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [importCustomersAll, setImportCustomersAll] = useState([]);
  const [importCustomersLoading, setImportCustomersLoading] = useState(false);
  const [partyDeductions, setPartyDeductions] = useState({
    byBrand: {},
    totalOe: 0,
    totalOrd: 0,
  });
  const [partyDedLoading, setPartyDedLoading] = useState(false);
  const [specialGroupLines, setSpecialGroupLines] = useState([]);
  const [specialGroupLoading, setSpecialGroupLoading] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [savedSnapshotAt, setSavedSnapshotAt] = useState("");
  const savedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);
  const visibleBranchOptions = useMemo(() => {
    const visible = getVisibleAppBranches(savedUser);
    if (Array.isArray(visible) && visible.length > 0) return visible;
    return BRANCH_OPTIONS;
  }, [savedUser]);

  useEffect(() => {
    const allowed = new Set(visibleBranchOptions.map((b) => b.value));
    if (!allowed.has(branchFilter)) {
      setBranchFilter(visibleBranchOptions[0]?.value || "Main");
    }
  }, [branchFilter, visibleBranchOptions]);

  // On branch change: render from local cache immediately, then subscribe to
  // Firestore for the cloud-synced copy. Edits made on any other PC arrive
  // here automatically (echoes from our own writes are ignored via writeId).
  useEffect(() => {
    const cached = loadConfig(branchFilter);
    setConfig(cached);
    configRef.current = cached;

    // Local-cache accountant phone (shown until the cloud copy arrives)
    try {
      const raw = localStorage.getItem(LS_ACCOUNTANT_PHONE);
      const all = raw ? JSON.parse(raw) : {};
      const v = all && typeof all === "object" ? all[branchFilter] : "";
      setAccountantPhoneRaw(v != null ? String(v) : "");
    } catch {
      setAccountantPhoneRaw("");
    }

    setCloudStatus({ state: "loading", error: "", savedAt: 0 });
    cloudWriteIdRef.current = "";

    const unsub = subscribeIncentiveConfig(
      branchFilter,
      (remote) => {
        if (remote == null) {
          // No cloud doc yet for this branch. If this PC has a local list
          // saved, push it once so the cloud copy is seeded.
          setCloudStatus({ state: "ok", error: "", savedAt: 0 });
          const localCopy = configRef.current;
          const hasLocalStaff = Array.isArray(localCopy?.staff)
            && localCopy.staff.some((r) => String(r?.employeeId || "").trim() || String(r?.name || "").trim());
          if (hasLocalStaff) {
            const writeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            cloudWriteIdRef.current = writeId;
            saveIncentiveConfigCloud(branchFilter, { ...localCopy, _writeId: writeId }).catch(() => {});
          }
          return;
        }

        // Skip echoes of our own writes (avoids clobbering edits made between
        // the local save and the Firestore round-trip).
        if (remote._writeId && remote._writeId === cloudWriteIdRef.current) {
          setCloudStatus({ state: "ok", error: "", savedAt: Date.now() });
          return;
        }

        const merged = normalizeIncentiveConfig(remote);
        setConfig(merged);
        configRef.current = merged;
        // Refresh local cache so other tabs / offline reloads see the latest.
        saveConfig(branchFilter, merged);

        if (typeof remote.accountantPhone === "string") {
          setAccountantPhoneRaw(remote.accountantPhone);
          try {
            const raw = localStorage.getItem(LS_ACCOUNTANT_PHONE);
            const all = raw ? JSON.parse(raw) : {};
            const next = all && typeof all === "object" ? { ...all } : {};
            next[branchFilter] = remote.accountantPhone;
            localStorage.setItem(LS_ACCOUNTANT_PHONE, JSON.stringify(next));
          } catch {
            /* ignore */
          }
        }

        setCloudStatus({ state: "ok", error: "", savedAt: Date.now() });
      },
      (err) => {
        setCloudStatus({
          state: "error",
          error: err?.message || String(err),
          savedAt: 0,
        });
      }
    );

    return () => {
      try { unsub?.(); } catch { /* ignore */ }
    };
  }, [branchFilter]);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setEmployeesLoading(true);
      setEmployeesError(null);
      setEmployees([]);
      try {
        const idx = BILLING_BRANCH_TO_ATTENDANCE_INDEX[branchFilter] ?? 0;
        const branchCfg = ATTENDANCE_BRANCHES[idx] || ATTENDANCE_BRANCHES[0];
        const colName = await resolveCollectionName(branchCfg.employeeCandidates || []);
        if (!colName) throw new Error("Employee collection not found for this branch.");
        const snap = await getDocs(collection(db, colName));
        if (cancelled) return;
        const merged = new Map();
        for (const docSnap of snap.docs) {
          const rec = employeeRecordFromDoc(docSnap);
          if (!rec.employeeId) continue;
          const key = rec.employeeId.toLowerCase();
          if (!merged.has(key)) merged.set(key, rec);
        }
        setEmployees([...merged.values()].sort((a, b) => a.employeeName.localeCompare(b.employeeName)));
      } catch (e) {
        if (!cancelled) {
          setEmployeesError(e?.message || String(e));
          setEmployees([]);
        }
      } finally {
        if (!cancelled) setEmployeesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchFilter]);

  const scheduleCloudSave = useCallback(
    (branch, snapshot) => {
      if (cloudSaveTimerRef.current) {
        clearTimeout(cloudSaveTimerRef.current);
        cloudSaveTimerRef.current = null;
      }
      cloudSaveTimerRef.current = setTimeout(async () => {
        cloudSaveTimerRef.current = null;
        cloudSaveBusyRef.current = true;
        try {
          const writeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          cloudWriteIdRef.current = writeId;
          await saveIncentiveConfigCloud(branch, { ...snapshot, _writeId: writeId });
          setCloudStatus({ state: "ok", error: "", savedAt: Date.now() });
        } catch (e) {
          console.error("[monthlyIncentive] cloud save failed", e);
          setCloudStatus({
            state: "error",
            error: e?.message || String(e),
            savedAt: 0,
          });
        } finally {
          cloudSaveBusyRef.current = false;
        }
      }, 700);
    },
    []
  );

  /** After directory loads, refresh stored display names for any employeeId rows. */
  useEffect(() => {
    if (!employees.length) return;
    setConfig((prev) => {
      const staff = prev.staff || [];
      let changed = false;
      const nextStaff = staff.map((row) => {
        const id = String(row.employeeId || "").trim();
        if (!id) return row;
        const emp = employees.find((e) => e.employeeId === id);
        if (!emp || row.name === emp.employeeName) return row;
        changed = true;
        return { ...row, name: emp.employeeName };
      });
      if (!changed) return prev;
      const next = { ...prev, staff: nextStaff };
      saveConfig(branchFilter, next);
      configRef.current = next;
      scheduleCloudSave(branchFilter, next);
      return next;
    });
  }, [employees, branchFilter, scheduleCloudSave]);

  const { calendarYear: year, monthIndex } = getCalendarMonthFromFySlot(fyStartYear, fySlot);
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const fyMonthSequence = useMemo(() => getFinancialYearMonthSequence(fyStartYear), [fyStartYear]);
  const periodLabel = `${MONTH_OPTIONS[monthIndex]} ${year}`;

  const reloadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await loadMonthSalePageData(year, monthIndex, branchFilter, {
        importOnly: true,
        skipPrecalc: true,
      });
      setData(d);
    } catch (e) {
      setError(e?.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, monthIndex, branchFilter]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  useEffect(() => {
    let cancelled = false;
    setImportCustomersLoading(true);
    (async () => {
      try {
        const names = await fetchImportCustomerNamesForIncentive({
          year,
          monthIndex,
          branchFilter,
        });
        if (!cancelled) setImportCustomersAll(names);
      } catch {
        if (!cancelled) setImportCustomersAll([]);
      } finally {
        if (!cancelled) setImportCustomersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex, branchFilter]);

  const importCustomers = useMemo(() => {
    const term = String(customerSearch || "").trim().toLowerCase();
    if (!term) return importCustomersAll;
    return importCustomersAll.filter((n) => n.toLowerCase().includes(term));
  }, [importCustomersAll, customerSearch]);

  useEffect(() => {
    let cancelled = false;
    const parties = config.deductParties || [];
    if (!parties.length) {
      setPartyDeductions({ byBrand: {}, totalOe: 0, totalOrd: 0 });
      return undefined;
    }
    setPartyDedLoading(true);
    (async () => {
      try {
        const result = await fetchPartyDeductionsForIncentive({
          year,
          monthIndex,
          branchFilter,
          partyNames: parties,
        });
        if (!cancelled) setPartyDeductions(result);
      } catch {
        if (!cancelled) setPartyDeductions({ byBrand: {}, totalOe: 0, totalOrd: 0 });
      } finally {
        if (!cancelled) setPartyDedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex, branchFilter, config.deductParties]);

  useEffect(() => {
    let cancelled = false;
    if (!EMPLOYEE_SPECIAL_GROUP_INCENTIVE_PLANS.length || branchFilter === SPAREWAY_BRANCH_VALUE) {
      setSpecialGroupLines([]);
      setSpecialGroupLoading(false);
      return undefined;
    }
    setSpecialGroupLoading(true);
    (async () => {
      try {
        const result = await fetchSpecialGroupIncentiveBundle({
          year,
          monthIndex,
          branchFilter,
          plans: EMPLOYEE_SPECIAL_GROUP_INCENTIVE_PLANS,
          employees,
          staffRows: config.staff || [],
        });
        if (!cancelled) setSpecialGroupLines(result.lines || []);
      } catch {
        if (!cancelled) setSpecialGroupLines([]);
      } finally {
        if (!cancelled) setSpecialGroupLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex, branchFilter, employees, config.staff]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await loadMonthlyIncentiveSnapshot({ year, monthIndex, branchFilter });
        if (!cancelled) setSavedSnapshotAt(snap?.generatedAt ? String(snap.generatedAt) : "");
      } catch {
        if (!cancelled) setSavedSnapshotAt("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex, branchFilter]);

  // Flush any pending cloud save when the page unmounts so quick edits before
  // navigating away still reach Firestore.
  useEffect(() => {
    return () => {
      if (cloudSaveTimerRef.current) {
        clearTimeout(cloudSaveTimerRef.current);
        cloudSaveTimerRef.current = null;
        const branch = branchFilter;
        const snapshot = configRef.current;
        const writeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        cloudWriteIdRef.current = writeId;
        saveIncentiveConfigCloud(branch, { ...snapshot, _writeId: writeId }).catch(() => {});
      }
    };
  }, [branchFilter]);

  const persistConfig = useCallback(
    (nextOrUpdater) => {
      setConfig((prev) => {
        const next = typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;
        saveConfig(branchFilter, next);
        configRef.current = next;
        scheduleCloudSave(branchFilter, next);
        return next;
      });
    },
    [branchFilter, scheduleCloudSave]
  );

  const branchLabel = BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label ?? branchFilter;
  const isSparewayBranch = branchFilter === SPAREWAY_BRANCH_VALUE;

  const saveIncentiveConfigExplicit = useCallback(
    async (loadingMsg, successMsg) => {
      const snap = configRef.current;
      saveConfig(branchFilter, snap);
      if (cloudSaveTimerRef.current) {
        clearTimeout(cloudSaveTimerRef.current);
        cloudSaveTimerRef.current = null;
      }
      const writeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      cloudWriteIdRef.current = writeId;
      const tId = toast.loading(loadingMsg || `Saving for ${branchLabel}…`);
      try {
        await saveIncentiveConfigCloud(branchFilter, { ...snap, _writeId: writeId });
        setCloudStatus({ state: "ok", error: "", savedAt: Date.now() });
        toast.success(successMsg || `Saved for ${branchLabel}. Synced to all PCs.`, { id: tId });
      } catch (e) {
        console.error("[monthlyIncentive] explicit save failed", e);
        setCloudStatus({
          state: "error",
          error: e?.message || String(e),
          savedAt: 0,
        });
        toast.error(
          `Saved on this PC but cloud sync failed: ${e?.message || e}. Other PCs won't see it until sync succeeds.`,
          { id: tId }
        );
      }
    },
    [branchFilter, branchLabel]
  );

  const saveStaffListExplicit = useCallback(
    () =>
      saveIncentiveConfigExplicit(
        `Saving Staff by brand for ${branchLabel}…`,
        `Staff by brand saved for ${branchLabel}. Synced to all PCs.`
      ),
    [saveIncentiveConfigExplicit, branchLabel]
  );

  const saveSparewaySettingsExplicit = useCallback(
    () =>
      saveIncentiveConfigExplicit(
        `Saving Spareway settings for ${branchLabel}…`,
        `Branch transfer and Spareway settings saved for ${branchLabel}.`
      ),
    [saveIncentiveConfigExplicit, branchLabel]
  );

  const exportStaffListJson = useCallback(() => {
    const snap = configRef.current;
    const payload = { version: 1, branch: branchFilter, exportedAt: new Date().toISOString(), staff: snap.staff || [] };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `monthly-incentive-staff-${branchFilter.replace(/\s+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Staff list exported");
  }, [branchFilter]);

  const importStaffListFromFile = useCallback(
    (file) => {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = String(reader.result || "");
          const o = JSON.parse(text);
          const raw = o?.staff ?? o;
          if (!Array.isArray(raw)) throw new Error('File must contain a "staff" array.');
          persistConfig((prev) => ({ ...prev, staff: normalizeStaffList(raw) }));
          toast.success("Staff list imported and saved.");
        } catch (e) {
          toast.error(e?.message || "Could not read file");
        }
      };
      reader.readAsText(file);
    },
    [persistConfig]
  );

  const brandMap = useMemo(() => {
    const m = new Map();
    if (!data?.brands?.length) return m;
    for (const b of data.brands) {
      if (b?.n) m.set(b.n, b);
    }
    return m;
  }, [data]);

  const { rows, grandTotal, oePoolTotal, ordPoolTotal, oeRatePctNum, ordRatePctNum, sparewaySummary } = useMemo(() => {
    if (!data) {
      return {
        rows: [],
        grandTotal: 0,
        oePoolTotal: 0,
        ordPoolTotal: 0,
        oeRatePctNum: DEFAULT_OE_RATE_PCT,
        ordRatePctNum: DEFAULT_ORD_RATE_PCT,
        sparewaySummary: null,
      };
    }
    const div = Math.max(1, parseNum(config.miscDivisor) || DEFAULT_MISC_DIVISOR);
    const share = incentiveOrdinaryMiscSharePerBrand(data, brandMap, INCENTIVE_BRANDS, div);
    const oeRatePct = parseNum(config.oeRatePct) || DEFAULT_OE_RATE_PCT;
    const ordRatePct = parseNum(config.ordRatePct) || DEFAULT_ORD_RATE_PCT;

    const out = [];
    let total = 0;
    if (isSparewayBranch) {
      const sparewayOeMin = parseNum(config.sparewayOeMinSale) || SPAREWAY_OE_MIN_SALE;
      const sparewayOrdMin = parseNum(config.sparewayOrdMinSale) || SPAREWAY_ORD_MIN_SALE;
      const oeBrTransfer = parseNum(config.sparewayBrTransferOe);
      const ordBrTransfer = parseNum(config.sparewayBrTransferOrd);
      const miscAsOrdinary = Number(data.miscSale) || 0;

      const brandLines = [];
      let computedOeSalesTotal = 0;
      let ordSalesFromBrands = 0;
      for (const brandId of INCENTIVE_BRANDS) {
        const row = brandMap.get(brandId);
        if (!row) continue;
        const partyOe = partyDeductions.byBrand?.[brandId]?.oe || 0;
        const partyOrd = partyDeductions.byBrand?.[brandId]?.ord || 0;
        const grossOe = grossOeSaleForBrand(row);
        const grossOrd = grossOrdinarySaleForBrand(row);
        const computedOe = Math.max(0, grossOe - partyOe);
        const computedOrd = Math.max(0, grossOrd - partyOrd);
        computedOeSalesTotal += computedOe;
        ordSalesFromBrands += computedOrd;
        const oeSale = sparewayBrandSaleValue(config, brandId, "oe", computedOe);
        const ordSale = sparewayBrandSaleValue(config, brandId, "ord", computedOrd);
        brandLines.push({
          brandId,
          row,
          grossOe,
          grossOrd,
          partyOe,
          partyOrd,
          computedOe,
          computedOrd,
          oeSale,
          ordSale,
          manualOe: sparewayBrandSaleIsManual(config, brandId, "oe"),
          manualOrd: sparewayBrandSaleIsManual(config, brandId, "ord"),
        });
      }

      const ordBlankRemainder = Math.max(0, (Number(data.ordinaryAmt) || 0) - ordSalesFromBrands);
      const computedOrdSalesTotal = ordSalesFromBrands + ordBlankRemainder + miscAsOrdinary;
      const oeSalesTotal = brandLines.reduce((s, b) => s + (Number(b.oeSale) || 0), 0);
      const ordSalesTotal =
        brandLines.reduce((s, b) => s + (Number(b.ordSale) || 0), 0) + ordBlankRemainder + miscAsOrdinary;
      // Spareway: Eligible = Total sale − Threshold − Branch transfer (min 0).
      const oeEligible = Math.max(0, oeSalesTotal - sparewayOeMin - oeBrTransfer);
      const ordEligible = Math.max(0, ordSalesTotal - sparewayOrdMin - ordBrTransfer);
      const oePool = (oeEligible * SPAREWAY_OE_RATE_PCT) / 100;
      const ordPool = (ordEligible * SPAREWAY_ORD_RATE_PCT) / 100;
      total = oePool + ordPool;
      for (const b of brandLines) {
        const { brandId, row, grossOe, grossOrd, partyOe, partyOrd, computedOe, computedOrd, oeSale, ordSale } = b;
        const brandRemainder = brandSaleRemainder(row);
        const oeShare = oeSalesTotal > 0 ? oeSale / oeSalesTotal : 0;
        const ordShare = ordSalesTotal > 0 ? ordSale / ordSalesTotal : 0;
        const oeIncentive = oePool * oeShare;
        const ordIncentive = ordPool * ordShare;
        out.push({
          brandId,
          brandTotal: Number(row.c) || 0,
          brandRemainder,
          grossOe,
          grossOrd,
          partyOe,
          partyOrd,
          computedOe,
          computedOrd,
          oeSale,
          ordSale,
          brOe: 0,
          brOrd: 0,
          oeRet: 0,
          ordRet: 0,
          minOe: sparewayOeMin,
          minOrd: sparewayOrdMin,
          miscShare: 0,
          netOe: oeSale,
          netOeForRate: oeSale,
          oeIncentive,
          netOrd: ordSale,
          netOrdForRate: ordSale,
          ordIncentive,
          section: oeIncentive + ordIncentive,
          manualOe: b.manualOe,
          manualOrd: b.manualOrd,
        });
      }
      return {
        rows: out,
        grandTotal: total,
        oePoolTotal: oePool,
        ordPoolTotal: ordPool,
        oeRatePctNum: SPAREWAY_OE_RATE_PCT,
        ordRatePctNum: SPAREWAY_ORD_RATE_PCT,
        sparewaySummary: {
          oeSalesTotal,
          ordSalesTotal,
          computedOeSalesTotal,
          computedOrdSalesTotal,
          miscAsOrdinary,
          ordBlankRemainder,
          oeBrTransfer,
          ordBrTransfer,
          oeEligible,
          ordEligible,
          oePool,
          ordPool,
          oeMinSale: sparewayOeMin,
          ordMinSale: sparewayOrdMin,
          manualBrandSales: brandLines.some((b) => b.manualOe || b.manualOrd),
        },
      };
    }
    for (const brandId of INCENTIVE_BRANDS) {
      const row = brandMap.get(brandId);
      if (!row) continue;
      const partyOe = partyDeductions.byBrand?.[brandId]?.oe || 0;
      const partyOrd = partyDeductions.byBrand?.[brandId]?.ord || 0;
      const grossOe = grossOeSaleForBrand(row);
      const brandRemainder = brandSaleRemainder(row);
      const grossOrd = grossOrdinarySaleForBrand(row);
      const oeSale = Math.max(0, grossOe - partyOe);
      const ordSale = Math.max(0, grossOrd - partyOrd);
      const cuts = config.brandCuts[brandId] || {};
      const brOe = parseNum(cuts.oe);
      const brOrd = parseNum(cuts.ord);
      const mins = config.mins[brandId] || {};
      const minOe = parseNum(mins.oeMin);
      const minOrd = parseNum(mins.ordMin);
      const { oe: oeRet, ord: ordRet } = oeOrdSaleReturnAmounts(data, brandId, row);
      const { netOe, netOeForRate, oeIncentive } = computeOeIncentive(oeSale, brOe, oeRet, minOe, oeRatePct);
      const { netOrd, netOrdForRate, ordIncentive } = computeOrdinaryIncentive(
        ordSale,
        brOrd,
        share,
        ordRet,
        minOrd,
        ordRatePct
      );
      const section = oeIncentive + ordIncentive;
      total += section;
      out.push({
        brandId,
        brandTotal: Number(row.c) || 0,
        brandRemainder,
        grossOe,
        grossOrd,
        partyOe,
        partyOrd,
        oeSale,
        ordSale,
        brOe,
        brOrd,
        oeRet,
        ordRet,
        minOe,
        minOrd,
        miscShare: share,
        netOe,
        netOeForRate,
        oeIncentive,
        netOrd,
        netOrdForRate,
        ordIncentive,
        section,
      });
    }
    const oePoolTotal = out.reduce((s, r) => s + (Number(r.oeIncentive) || 0), 0);
    const ordPoolTotal = out.reduce((s, r) => s + (Number(r.ordIncentive) || 0), 0);
    return {
      rows: out,
      grandTotal: total,
      oePoolTotal,
      ordPoolTotal,
      oeRatePctNum: oeRatePct,
      ordRatePctNum: ordRatePct,
      sparewaySummary: null,
    };
  }, [data, brandMap, config, partyDeductions, isSparewayBranch]);

  const staffComputed = useMemo(() => {
    const list = config.staff || [];
    const sectionMap = {};
    for (const r of rows) sectionMap[r.brandId] = Number(r.section) || 0;

    const sumPctByBrand = {};
    for (const bid of INCENTIVE_BRANDS) sumPctByBrand[bid] = 0;
    let sumPctAll = 0;
    for (const s of list) {
      const p = Math.max(0, parseNum(s.pct));
      sumPctAll += p;
      const bid = String(s.brandId || "").trim();
      if (bid && INCENTIVE_BRANDS.includes(bid)) sumPctByBrand[bid] += p;
    }
    const sparewaySection = grandTotal;

    const prelines = list.map((s, index) => {
      const bid = String(s.brandId || "").trim();
      const pct = Math.max(0, parseNum(s.pct));
      const leave = Math.max(0, parseNum(s.leave));
      const cash = isCashBrandingRow(s);
      const autoLeaveCutEnabled = isAutoLeaveCutEnabledRow(s);
      const leaveCashCutEnabled = isLeaveCashCutEnabledRow(s);
      const leaveCashCutAmt = leaveCashCutAmtRow(s);
      const section = isSparewayBranch ? sparewaySection : bid ? sectionMap[bid] ?? 0 : 0;
      const sumPct = isSparewayBranch ? sumPctAll : bid ? sumPctByBrand[bid] ?? 0 : 0;
      let base = 0;
      if (section > 0 && pct > 0 && sumPct > 0) {
        if (sumPct > 100) base = (section * pct) / sumPct;
        else base = (section * pct) / 100;
      }
      const autoCut = autoLeaveCutEnabled ? (base / LEAVE_DAYS_IN_MONTH) * leave : 0;
      const manualCut = leaveCashCutEnabled ? leaveCashCutAmt : 0;
      const cut = autoCut + manualCut;
      return {
        index,
        name: String(s?.name ?? "").trim(),
        brandId: bid,
        pct,
        leave,
        base,
        autoCut,
        manualCut,
        cut,
        cash,
        autoLeaveCutEnabled,
        leaveCashCutEnabled,
        leaveCashCutAmt,
      };
    });

    const bonusByIndex = list.map(() => 0);
    if (isSparewayBranch) {
      const totalCutPool = prelines.reduce((a, L) => a + (Number(L.autoCut) || 0), 0);
      if (totalCutPool > 0) {
        let elig = prelines.filter((L) => L.cash);
        if (!elig.length) elig = prelines;
        const shares = splitByWeights(
          totalCutPool,
          elig.map((L) => ({ key: L.index, weight: Math.max(1, Number(L.base) || 0) }))
        );
        for (const L of elig) bonusByIndex[L.index] += shares.get(L.index) ?? 0;
      }
    } else {
      const brandsInStaff = [...new Set(prelines.map((L) => L.brandId).filter(Boolean))];
      for (const bid of brandsInStaff) {
        const onBrand = prelines.filter((L) => L.brandId === bid);
        // Only automatic leave cut is pooled. Manual cut is personal and not shared.
        const totalCutPool = onBrand.reduce((a, L) => a + (Number(L.autoCut) || 0), 0);
        if (totalCutPool <= 0) continue;
        let elig = onBrand.filter((L) => L.cash);
        if (!elig.length) elig = onBrand;
        const shares = splitByWeights(
          totalCutPool,
          elig.map((L) => ({ key: L.index, weight: Math.max(1, Number(L.base) || 0) }))
        );
        for (const L of elig) {
          bonusByIndex[L.index] += shares.get(L.index) ?? 0;
        }
      }
    }

    const lines = prelines.map((L) => {
      const poolShare = bonusByIndex[L.index] ?? 0;
      const finalAmt = L.base - L.cut + poolShare;
      return {
        index: L.index,
        name: L.name,
        brandId: L.brandId,
        pct: L.pct,
        leave: L.leave,
        base: L.base,
        autoCut: L.autoCut,
        manualCut: L.manualCut,
        cut: L.cut,
        poolShare,
        finalAmt,
      };
    });

    const unassignedByBrand = {};
    const brandsPctOver100 = [];
    let brandsWithNoPct = [];
    if (isSparewayBranch) {
      const unassignedTotal = sparewaySection > 0 && sumPctAll <= 100 ? (sparewaySection * (100 - sumPctAll)) / 100 : 0;
      const firstBid = rows[0]?.brandId || "TOTAL";
      for (const r of rows) unassignedByBrand[r.brandId] = 0;
      unassignedByBrand[firstBid] = unassignedTotal;
      if (sumPctAll > 100) brandsPctOver100.push("ALL");
      if (sparewaySection > 0 && sumPctAll <= 0) brandsWithNoPct = ["ALL"];
    } else {
      for (const r of rows) {
        const bid = r.brandId;
        const section = Number(r.section) || 0;
        const sp = sumPctByBrand[bid] ?? 0;
        if (sp > 100) brandsPctOver100.push(bid);
        if (section <= 0) unassignedByBrand[bid] = 0;
        else if (sp > 100) unassignedByBrand[bid] = 0;
        else unassignedByBrand[bid] = (section * (100 - sp)) / 100;
      }
      brandsWithNoPct = rows
        .filter((r) => (sectionMap[r.brandId] || 0) > 0 && (sumPctByBrand[r.brandId] || 0) <= 0)
        .map((r) => r.brandId);
    }

    const totalFinal = lines.reduce((a, L) => a + L.finalAmt, 0);
    const totalUnassigned = rows.reduce((a, r) => a + (unassignedByBrand[r.brandId] ?? 0), 0);

    return { lines, unassignedByBrand, brandsPctOver100, brandsWithNoPct, totalFinal, totalUnassigned, sumPctByBrand };
  }, [config.staff, rows, grandTotal, isSparewayBranch]);

  const specialGroupTotal = useMemo(
    () => specialGroupLines.reduce((a, L) => a + (Number(L.incentiveAmount) || 0), 0),
    [specialGroupLines]
  );

  /** VINOD.MENJERI (etc.): incentive = rate% of all other staff net pay. */
  const staffPoolPctOverrides = useMemo(() => {
    if (!EMPLOYEE_STAFF_POOL_PCT_PLANS.length) return [];

    /** @type {Map<string, { name: string, employeeId: string, plan: object, brandFinal: number, groupFinal: number }>} */
    const byPlanName = new Map();
    const touch = (name, employeeId) => {
      const plan = getStaffPoolPctPlanForPerson({ name, employeeId });
      if (!plan) return null;
      const key = String(plan.name || "").trim();
      if (!key) return null;
      if (!byPlanName.has(key)) {
        byPlanName.set(key, {
          name: key,
          employeeId: String(employeeId || "").trim(),
          plan,
          brandFinal: 0,
          groupFinal: 0,
        });
      } else if (employeeId && !byPlanName.get(key).employeeId) {
        byPlanName.get(key).employeeId = String(employeeId).trim();
      }
      return key;
    };

    for (const L of staffComputed.lines) {
      const row = config.staff[L.index];
      const key = touch(L.name, row?.employeeId);
      if (!key) continue;
      byPlanName.get(key).brandFinal += Number(L.finalAmt) || 0;
    }
    for (const G of specialGroupLines) {
      const key = touch(G.employeeName, G.employeeId);
      if (!key) continue;
      byPlanName.get(key).groupFinal += Number(G.incentiveAmount) || 0;
    }
    for (const emp of employees) {
      touch(emp.employeeName, emp.employeeId);
    }
    for (const plan of EMPLOYEE_STAFF_POOL_PCT_PLANS) {
      touch(plan.name, (plan.employeeIds || [])[0] || "");
    }

    const allStaffPlusGroup = staffComputed.totalFinal + specialGroupTotal;
    const out = [];
    for (const row of byPlanName.values()) {
      const excluded = (Number(row.brandFinal) || 0) + (Number(row.groupFinal) || 0);
      const othersTotal = Math.max(0, allStaffPlusGroup - excluded);
      const amount = computeStaffPoolPctIncentive(othersTotal, row.plan.ratePct);
      out.push({
        name: row.name,
        employeeId: row.employeeId,
        ratePct: row.plan.ratePct,
        othersTotal,
        amount,
        replacedBrandFinal: Number(row.brandFinal) || 0,
        replacedGroupFinal: Number(row.groupFinal) || 0,
      });
    }
    return out;
  }, [staffComputed.lines, staffComputed.totalFinal, specialGroupLines, specialGroupTotal, config.staff, employees]);

  const isStaffPoolPctLine = useCallback(
    (name, employeeId) => Boolean(getStaffPoolPctPlanForPerson({ name, employeeId })),
    []
  );

  const staffPoolPctTotal = useMemo(
    () => staffPoolPctOverrides.reduce((a, o) => a + (Number(o.amount) || 0), 0),
    [staffPoolPctOverrides]
  );
  const staffPoolPctReplaced = useMemo(
    () =>
      staffPoolPctOverrides.reduce(
        (a, o) => a + (Number(o.replacedBrandFinal) || 0) + (Number(o.replacedGroupFinal) || 0),
        0
      ),
    [staffPoolPctOverrides]
  );

  const totalFinalCombined =
    staffComputed.totalFinal + specialGroupTotal - staffPoolPctReplaced + staffPoolPctTotal;

  const metrics = useMemo(() => {
    const lines = staffComputed.lines;
    const totalGross = lines.reduce((a, L) => a + L.base, 0);
    const totalLeaveCuts = lines.reduce((a, L) => a + (Number(L.autoCut) || 0), 0);
    const totalCutAmount = lines.reduce((a, L) => a + (Number(L.manualCut) || 0), 0);
    return { totalGross, totalLeaveCuts, totalCutAmount };
  }, [staffComputed.lines]);

  const personTotals = useMemo(() => {
    const m = new Map();
    for (const L of staffComputed.lines) {
      const key = L.name.trim();
      if (!key) continue;
      const row = config.staff[L.index];
      if (isStaffPoolPctLine(key, row?.employeeId)) continue;
      m.set(key, (m.get(key) || 0) + L.finalAmt);
    }
    for (const G of specialGroupLines) {
      const key = String(G.employeeName || G.employeeId || "").trim();
      if (!key) continue;
      if (isStaffPoolPctLine(key, G.employeeId)) continue;
      m.set(key, (m.get(key) || 0) + (Number(G.incentiveAmount) || 0));
    }
    for (const o of staffPoolPctOverrides) {
      m.set(o.name, Number(o.amount) || 0);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [staffComputed.lines, specialGroupLines, staffPoolPctOverrides, config.staff, isStaffPoolPctLine]);

  const personBreakdownByName = useMemo(() => {
    const m = new Map();
    for (const L of staffComputed.lines) {
      const key = L.name.trim();
      if (!key) continue;
      const row = config.staff[L.index];
      if (isStaffPoolPctLine(key, row?.employeeId)) continue;
      const curr = m.get(key) || {
        gross: 0,
        autoCut: 0,
        manualCut: 0,
        net: 0,
        groupSale: 0,
        groupIncentive: 0,
        totalPct: 0,
        staffPoolPct: false,
      };
      curr.gross += Number(L.base) || 0;
      curr.autoCut += Number(L.autoCut) || 0;
      curr.manualCut += Number(L.manualCut) || 0;
      curr.net += Number(L.finalAmt) || 0;
      curr.totalPct += Math.max(0, parseNum(L.pct));
      m.set(key, curr);
    }
    for (const G of specialGroupLines) {
      const key = String(G.employeeName || G.employeeId || "").trim();
      if (!key) continue;
      if (isStaffPoolPctLine(key, G.employeeId)) continue;
      const curr = m.get(key) || {
        gross: 0,
        autoCut: 0,
        manualCut: 0,
        net: 0,
        groupSale: 0,
        groupIncentive: 0,
        totalPct: 0,
        staffPoolPct: false,
      };
      curr.groupSale += Number(G.saleAmount) || 0;
      curr.groupIncentive += Number(G.incentiveAmount) || 0;
      curr.net += Number(G.incentiveAmount) || 0;
      m.set(key, curr);
    }
    for (const o of staffPoolPctOverrides) {
      m.set(o.name, {
        gross: Number(o.amount) || 0,
        autoCut: 0,
        manualCut: 0,
        net: Number(o.amount) || 0,
        groupSale: 0,
        groupIncentive: 0,
        totalPct: Number(o.ratePct) || 0,
        staffPoolPct: true,
        othersTotal: Number(o.othersTotal) || 0,
      });
    }
    return m;
  }, [staffComputed.lines, specialGroupLines, staffPoolPctOverrides, config.staff, isStaffPoolPctLine]);

  const personTotalsForSave = useMemo(
    () =>
      personTotals.map(([name, amt]) => {
        const parts = personBreakdownByName.get(name) || {};
        return {
          name,
          amount: Number(amt) || 0,
          totalPct: Number(parts.totalPct) || 0,
          staffPoolPct: Boolean(parts.staffPoolPct),
          othersTotal: parts.staffPoolPct ? Number(parts.othersTotal) || 0 : undefined,
        };
      }),
    [personTotals, personBreakdownByName]
  );

  const phoneDigitsByEmployeeIdLower = useMemo(() => {
    const m = new Map();
    for (const e of employees) {
      const id = String(e.employeeId || "").trim().toLowerCase();
      if (!id) continue;
      m.set(id, sanitizePhoneForWhatsApp(e.phone));
    }
    return m;
  }, [employees]);

  const buildNetPayWhatsAppText = useCallback(
    (personName, autoCut, manualCut, net, totalPct = 0, staffPoolPctMeta = null) =>
      [
        "Dear " + personName + ",",
        "",
        "Please find your Monthly Sales Incentive details below:",
        `Period: ${periodLabel}`,
        `Branch: ${branchLabel}`,
        `Financial Year: ${fyLabel}`,
        "",
        staffPoolPctMeta
          ? `Incentive: ${fmtPct(staffPoolPctMeta.ratePct)} of all other staff (base ${fmt(staffPoolPctMeta.othersTotal)})`
          : totalPct > 0
            ? `Total employee incentive %: ${fmtPct(totalPct)}`
            : null,
        staffPoolPctMeta ? null : `Leave cut: ${fmt(autoCut)}`,
        staffPoolPctMeta ? null : `Late coming cut amount: ${fmt(manualCut)}`,
        `Net Payable Incentive: ${fmt(net)}`,
        "",
        "Kindly review and contact Accounts for any clarification.",
        "",
        "Regards,",
        "Accounts Department",
      ]
        .filter(Boolean)
        .join("\n"),
    [periodLabel, branchLabel, fyLabel]
  );

  const copyNetPayWhatsAppText = useCallback(async (message) => {
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("Clipboard is not supported");
      await navigator.clipboard.writeText(message);
      toast.success("WhatsApp message copied.");
    } catch {
      toast.error("Could not copy message.");
    }
  }, []);

  const accountantPhoneDigits = useMemo(
    () => sanitizePhoneForWhatsApp(accountantPhoneRaw),
    [accountantPhoneRaw]
  );

  const saveAccountantPhone = useCallback(async () => {
    try {
      const raw = localStorage.getItem(LS_ACCOUNTANT_PHONE);
      const all = raw ? JSON.parse(raw) : {};
      const next = all && typeof all === "object" ? { ...all } : {};
      next[branchFilter] = accountantPhoneRaw;
      localStorage.setItem(LS_ACCOUNTANT_PHONE, JSON.stringify(next));
    } catch {
      /* local cache failure should not block cloud save */
    }
    const tId = toast.loading("Saving accountant number…");
    try {
      await saveAccountantPhoneCloud(branchFilter, accountantPhoneRaw);
      toast.success("Accountant number saved. Synced to all PCs.", { id: tId });
    } catch (e) {
      console.error("[monthlyIncentive] accountant phone cloud save failed", e);
      toast.error(
        `Saved on this PC but cloud sync failed: ${e?.message || e}.`,
        { id: tId }
      );
    }
  }, [accountantPhoneRaw, branchFilter]);

  const buildNetPaySummaryForAccountantText = useCallback(() => {
    const lines = [];
    lines.push(`Monthly Sales Incentive — Net Pay (Name-wise)`);
    lines.push(`Period: ${periodLabel}`);
    lines.push(`Branch: ${branchLabel}`);
    lines.push(`Financial Year: ${fyLabel}`);
    lines.push("");
    for (const [name, amt] of personTotals) {
      const totalPct = personBreakdownByName.get(name)?.totalPct ?? 0;
      lines.push(`${name}: ${fmtPct(totalPct)} · ${fmt(amt)}`);
    }
    lines.push("");
    lines.push(`Total Net Pay (all staff): ${fmt(totalFinalCombined)}`);
    return lines.join("\n");
  }, [periodLabel, branchLabel, fyLabel, personTotals, personBreakdownByName, totalFinalCombined]);

  const accountantImageFileName = useMemo(
    () =>
      `monthly-incentive-net-pay-${year}-${String(monthIndex + 1).padStart(2, "0")}-${branchFilter
        .replace(/\s+/g, "-")
        .toLowerCase()}.png`,
    [branchFilter, monthIndex, year]
  );

  useEffect(() => {
    return () => {
      if (accountantImageUrl) URL.revokeObjectURL(accountantImageUrl);
    };
  }, [accountantImageUrl]);

  const renderAccountantSummaryImage = useCallback(async () => {
    const el = accountantImageCardRef.current;
    if (!el) {
      toast.error("Image template is not ready.");
      return null;
    }
    try {
      setAccountantImageBusy(true);
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: Math.max(2, window.devicePixelRatio || 1),
        useCORS: true,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
      if (!blob) throw new Error("Could not generate PNG");
      return blob;
    } catch (e) {
      toast.error(e?.message || "Could not generate image.");
      return null;
    } finally {
      setAccountantImageBusy(false);
    }
  }, []);

  const ensureAccountantImageUrl = useCallback(async () => {
    const blob = await renderAccountantSummaryImage();
    if (!blob) return "";
    const nextUrl = URL.createObjectURL(blob);
    setAccountantImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return nextUrl;
    });
    return nextUrl;
  }, [renderAccountantSummaryImage]);

  const downloadAccountantImage = useCallback(async () => {
    const url = accountantImageUrl || (await ensureAccountantImageUrl());
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = accountantImageFileName;
    a.click();
    toast.success("Net pay image downloaded.");
  }, [accountantImageFileName, accountantImageUrl, ensureAccountantImageUrl]);

  const downloadAccountantExcel = useCallback(() => {
    try {
      const rowsForSheet = personTotalsForSave.map((row) => ({
        Name: row.name,
        "Total %": Number(row.totalPct) || 0,
        "Net Pay": Number(row.amount) || 0,
      }));
      rowsForSheet.push({ Name: "TOTAL", "Total %": "", "Net Pay": Number(totalFinalCombined) || 0 });

      const ws = XLSX.utils.json_to_sheet(rowsForSheet);
      ws["!cols"] = [{ wch: 32 }, { wch: 10 }, { wch: 16 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "NetPayByName");

      const fileName = `monthly-incentive-net-pay-${year}-${String(monthIndex + 1).padStart(2, "0")}-${branchFilter
        .replace(/\s+/g, "-")
        .toLowerCase()}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Net pay Excel downloaded.");
    } catch (e) {
      toast.error(e?.message || "Could not export Excel.");
    }
  }, [branchFilter, monthIndex, personTotalsForSave, totalFinalCombined, year]);

  const copyAccountantImage = useCallback(async () => {
    try {
      if (!navigator?.clipboard?.write || typeof window.ClipboardItem === "undefined") {
        throw new Error("Image copy is not supported in this browser");
      }
      const blob = await renderAccountantSummaryImage();
      if (!blob) return;
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Net pay image copied.");
    } catch (e) {
      toast.error(e?.message || "Could not copy image.");
    }
  }, [renderAccountantSummaryImage]);

  const openWhatsAppForAccountantImage = useCallback(async () => {
    if (!accountantImageUrl) {
      const generated = await ensureAccountantImageUrl();
      if (!generated) return;
    }
    const msg = "Monthly incentive net pay image is ready. Please find attached image.";
    window.open(whatsAppSendUrl(accountantPhoneDigits, msg), "_blank", "noopener,noreferrer");
    toast("WhatsApp opened. Attach the downloaded/copied image and send.", { icon: "🖼️" });
  }, [accountantImageUrl, accountantPhoneDigits, ensureAccountantImageUrl]);

  const phoneDigitsForNetPayName = useCallback(
    (personName) => {
      const trimmed = personName.trim();
      for (const L of staffComputed.lines) {
        if (L.name.trim() !== trimmed) continue;
        const row = config.staff[L.index];
        const eid = String(row?.employeeId || "").trim().toLowerCase();
        if (!eid) continue;
        const d = phoneDigitsByEmployeeIdLower.get(eid) || "";
        if (d) return d;
      }
      for (const G of specialGroupLines) {
        if (String(G.employeeName || "").trim() !== trimmed) continue;
        const eid = String(G.employeeId || "").trim().toLowerCase();
        if (!eid) continue;
        const d = phoneDigitsByEmployeeIdLower.get(eid) || "";
        if (d) return d;
      }
      for (const o of staffPoolPctOverrides) {
        if (o.name !== trimmed) continue;
        const eid = String(o.employeeId || "").trim().toLowerCase();
        if (eid) {
          const d = phoneDigitsByEmployeeIdLower.get(eid) || "";
          if (d) return d;
        }
        for (const e of employees) {
          if (!getStaffPoolPctPlanForPerson({ name: e.employeeName, employeeId: e.employeeId })) continue;
          const d = phoneDigitsByEmployeeIdLower.get(String(e.employeeId || "").trim().toLowerCase()) || "";
          if (d) return d;
        }
      }
      return "";
    },
    [
      staffComputed.lines,
      config.staff,
      phoneDigitsByEmployeeIdLower,
      specialGroupLines,
      staffPoolPctOverrides,
      employees,
    ]
  );

  const viewSnapshotHref = `/reports/monthly-incentive/view?${monthlySaleReportQuery(
    fyStartYear,
    fySlot,
    branchFilter
  )}`;

  const saveSnapshotToFirestore = useCallback(async () => {
    if (!rows.length) {
      toast.error("Load sales data and compute incentive before saving.");
      return;
    }
    setSnapshotSaving(true);
    const tId = toast.loading("Saving incentive snapshot to Firestore…");
    try {
      let userEmail = "";
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        userEmail = u?.email || "";
      } catch {
        /* ignore */
      }

      const payload = {
        year,
        monthIndex,
        fyStartYear,
        fySlot,
        branchFilter,
        branchLabel,
        periodLabel,
        fyLabel,
        isSparewayBranch,
        generatedAt: new Date().toISOString(),
        generatedBy: userEmail,
        oeRatePct: oeRatePctNum,
        ordRatePct: ordRatePctNum,
        deductParties: normalizeDeductParties(config.deductParties),
        partyDeductions,
        brandRows: rows,
        staffLines: staffComputed.lines.map((L) => ({
          ...L,
          employeeId: String(config.staff[L.index]?.employeeId || "").trim(),
        })),
        specialGroupLines,
        staffPoolPctOverrides,
        personTotals: personTotalsForSave,
        sparewaySummary,
        sparewayConfig: isSparewayBranch
          ? {
              sparewayBrTransferOe: config.sparewayBrTransferOe ?? "",
              sparewayBrTransferOrd: config.sparewayBrTransferOrd ?? "",
              sparewayOeMinSale: config.sparewayOeMinSale ?? "",
              sparewayOrdMinSale: config.sparewayOrdMinSale ?? "",
              sparewayBrandSales: config.sparewayBrandSales ?? {},
            }
          : null,
        summary: {
          grandTotal,
          oePoolTotal,
          ordPoolTotal,
          totalGross: metrics.totalGross,
          totalLeaveCuts: metrics.totalLeaveCuts,
          totalCutAmount: metrics.totalCutAmount,
          totalFinal: totalFinalCombined,
          totalStaffFinal: staffComputed.totalFinal,
          specialGroupTotal,
          staffPoolPctTotal,
          totalUnassigned: staffComputed.totalUnassigned,
          partyDedOe: partyDeductions.totalOe,
          partyDedOrd: partyDeductions.totalOrd,
        },
      };

      const result = await saveMonthlyIncentiveSnapshot(payload);
      setSavedSnapshotAt(result.generatedAt || "");
      toast.success(`Incentive saved for ${periodLabel} · ${branchLabel}.`, { id: tId });
    } catch (e) {
      console.error("[monthlyIncentive] snapshot save failed", e);
      toast.error(e?.message || String(e), { id: tId });
    } finally {
      setSnapshotSaving(false);
    }
  }, [
    rows,
    year,
    monthIndex,
    fyStartYear,
    fySlot,
    branchFilter,
    branchLabel,
    periodLabel,
    fyLabel,
    isSparewayBranch,
    oeRatePctNum,
    ordRatePctNum,
    config.deductParties,
    config.staff,
    config.sparewayBrTransferOe,
    config.sparewayBrTransferOrd,
    config.sparewayOeMinSale,
    config.sparewayOrdMinSale,
    config.sparewayBrandSales,
    partyDeductions,
    staffComputed.lines,
    staffComputed.totalFinal,
    staffComputed.totalUnassigned,
    specialGroupLines,
    specialGroupTotal,
    staffPoolPctOverrides,
    staffPoolPctTotal,
    totalFinalCombined,
    personTotalsForSave,
    sparewaySummary,
    grandTotal,
    oePoolTotal,
    ordPoolTotal,
    metrics.totalGross,
    metrics.totalLeaveCuts,
    metrics.totalCutAmount,
  ]);

  const downloadCsv = useCallback(() => {
    const lines = [];
    lines.push(`Monthly sale incentive — ${periodLabel} — ${branchLabel}`);
    lines.push(`FY ${fyLabel} (Apr–Mar)`);
    lines.push("");
    lines.push("Brand pool — OE (₹)");
    lines.push(["Brand", "OE incentive"].map(csvEscape).join(","));
    for (const r of rows) {
      lines.push([r.brandId, r.oeIncentive].map((x) => csvEscape(x)).join(","));
    }
    lines.push(["TOTAL OE", oePoolTotal].map(csvEscape).join(","));
    lines.push("");
    lines.push("Brand pool — Ordinary (₹)");
    lines.push(["Brand", "Ord. incentive"].map(csvEscape).join(","));
    for (const r of rows) {
      lines.push([r.brandId, r.ordIncentive].map((x) => csvEscape(x)).join(","));
    }
    lines.push(["TOTAL ORD", ordPoolTotal].map(csvEscape).join(","));
    lines.push(["GRAND TOTAL", grandTotal].map(csvEscape).join(","));
    lines.push("");
    lines.push("Staff lines");
    lines.push(
      ["Employee ID", "Name", "Brand", "%", "Leave", "Gross", "Leave cut", "Late coming cut amount", "Pool share", "Final"]
        .map(csvEscape)
        .join(",")
    );
    for (const L of staffComputed.lines) {
      const row = config.staff[L.index];
      const eid = row?.employeeId != null ? String(row.employeeId) : "";
      lines.push(
        [eid, L.name, L.brandId, L.pct, L.leave, L.base, L.autoCut, L.manualCut, L.poolShare, L.finalAmt]
          .map((x) => csvEscape(typeof x === "number" ? x : String(x)))
          .join(",")
      );
    }
    if (specialGroupLines.length) {
      lines.push("");
      lines.push("Special group incentive (1% of sale on tagged import lines)");
      lines.push(["Employee ID", "Name", "Group", "Sale", "Rate %", "Incentive"].map(csvEscape).join(","));
      for (const G of specialGroupLines) {
        lines.push(
          [G.employeeId, G.employeeName, G.groupId, G.saleAmount, G.ratePct, G.incentiveAmount]
            .map((x) => csvEscape(typeof x === "number" ? x : String(x)))
            .join(",")
        );
      }
      lines.push(["", "", "TOTAL", "", "", specialGroupTotal].map((x) => csvEscape(String(x))).join(","));
    }
    if (staffPoolPctOverrides.length) {
      lines.push("");
      lines.push("Staff pool % incentive (of all other staff)");
      lines.push(
        ["Employee", "Rate %", "Other staff total", "Incentive"].map(csvEscape).join(",")
      );
      for (const o of staffPoolPctOverrides) {
        lines.push(
          [o.name, o.ratePct, o.othersTotal, o.amount]
            .map((x) => csvEscape(typeof x === "number" ? x : String(x)))
            .join(",")
        );
      }
    }
    lines.push("");
    lines.push("Total by name");
    lines.push(["Name", "Total %", "Final total"].map(csvEscape).join(","));
    for (const row of personTotalsForSave) {
      lines.push([row.name, row.totalPct, row.amount].map((x) => csvEscape(String(x))).join(","));
    }
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `monthly-incentive-${year}-${String(monthIndex + 1).padStart(2, "0")}-${branchFilter.replace(/\s+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [
    branchFilter,
    branchLabel,
    fyLabel,
    grandTotal,
    oePoolTotal,
    ordPoolTotal,
    monthIndex,
    personTotalsForSave,
    periodLabel,
    rows,
    staffComputed.lines,
    specialGroupLines,
    specialGroupTotal,
    staffPoolPctOverrides,
    year,
    config.staff,
  ]);

  const setStaffField = (index, field, value) => {
    persistConfig((prev) => ({
      ...prev,
      staff: prev.staff.map((row, j) => (j === index ? { ...row, [field]: value } : row)),
    }));
  };

  const setStaffEmployee = (index, employeeId) => {
    const id = String(employeeId || "").trim();
    const emp = employees.find((e) => e.employeeId === id);
    persistConfig((prev) => ({
      ...prev,
      staff: prev.staff.map((row, j) => {
        if (j !== index) return row;
        if (!id) return { ...row, employeeId: "", name: "", leave: "" };
        return { ...row, employeeId: id, name: emp ? emp.employeeName : "" };
      }),
    }));
  };

  const setCut = (brandId, field, value) => {
    persistConfig((prev) => ({
      ...prev,
      brandCuts: {
        ...prev.brandCuts,
        [brandId]: { ...(prev.brandCuts[brandId] || {}), [field]: value },
      },
    }));
  };

  const setMin = (brandId, field, value) => {
    persistConfig((prev) => ({
      ...prev,
      mins: {
        ...prev.mins,
        [brandId]: { ...(prev.mins[brandId] || {}), [field]: value },
      },
    }));
  };

  const setSparewayBrandSale = (brandId, field, value) => {
    persistConfig((prev) => ({
      ...prev,
      sparewayBrandSales: {
        ...prev.sparewayBrandSales,
        [brandId]: { ...(prev.sparewayBrandSales?.[brandId] || {}), [field]: value },
      },
    }));
  };

  const setCalcParam = (field, value) => {
    persistConfig((prev) => ({ ...prev, [field]: value }));
  };

  const addDeductParty = () => {
    const name = String(customerSearch || "").trim();
    if (!name) {
      toast.error("Type or pick a customer from sales import first.");
      return;
    }
    const exact = importCustomersAll.find((n) => n.toLowerCase() === name.toLowerCase());
    const resolved = exact || name;
    persistConfig((prev) => {
      const existing = normalizeDeductParties(prev.deductParties);
      if (existing.some((p) => p.partyName.toUpperCase() === resolved.toUpperCase())) {
        return prev;
      }
      return {
        ...prev,
        deductParties: [...existing, { partyName: resolved, id: "" }],
      };
    });
    setCustomerSearch("");
    toast.success(`Customer added: ${resolved}`);
  };

  const removeDeductParty = (partyName) => {
    persistConfig((prev) => ({
      ...prev,
      deductParties: normalizeDeductParties(prev.deductParties).filter(
        (p) => p.partyName !== partyName
      ),
    }));
  };

  return (
    <>
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-100 via-slate-50/80 to-slate-100 print:bg-white print:min-h-0">
        <div className="w-full max-w-none px-2 sm:px-3 lg:px-4 py-6 sm:py-8 text-slate-800 print:py-4">
          <header className="rounded-2xl border border-slate-200/90 bg-white shadow-sm shadow-slate-200/50 overflow-hidden mb-6 print:shadow-none print:border-slate-300">
            <div className="border-b border-teal-900/10 bg-gradient-to-r from-teal-950 via-slate-900 to-slate-900 px-5 py-4 sm:px-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-widest text-teal-200/90">Incentives</p>
                  <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">Monthly sale incentive</h1>
                  <p className="text-sm text-slate-300 mt-1.5 max-w-2xl leading-relaxed">
                    <span className="text-teal-100/95">{periodLabel}</span>
                    <span className="text-slate-500 mx-2">·</span>
                    <span>FY {fyLabel}</span>
                    <span className="block sm:inline sm:mt-0 mt-1 text-slate-400 text-xs">
                      Sales from <strong className="text-slate-300 font-medium">sales import</strong> (OE from BrandName).{" "}
                      {isSparewayBranch
                        ? `Spareway: Eligible = Total sale − Threshold − Branch transfer; OE @ ${(oeRatePctNum).toFixed(1)}% · Ord. @ ${ordRatePctNum}%. Edit OE / Ord. sale per brand when import figures need adjustment.`
                        : `OE ${oeRatePctNum}% / Ord. ${ordRatePctNum}% of net.`}{" "}
                      Leave cut (gross ÷ {LEAVE_DAYS_IN_MONTH}) × days, pooled to cash-branding staff
                      {isSparewayBranch ? " across all staff lines." : " on the same brand."}
                    </span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 print:hidden">
                  <Link
                    to={viewSnapshotHref}
                    className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 hover:bg-white/15 no-underline"
                  >
                    View saved
                  </Link>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-400 disabled:opacity-50"
                    disabled={loading || snapshotSaving || !rows.length}
                    onClick={saveSnapshotToFirestore}
                  >
                    {snapshotSaving ? "Saving…" : "Save to Firestore"}
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 hover:bg-white/15"
                    onClick={() => window.print()}
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-teal-500 text-teal-950 text-sm font-medium hover:bg-teal-400 disabled:opacity-50"
                    disabled={loading || !rows.length}
                    onClick={downloadCsv}
                  >
                    Export CSV
                  </button>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 sm:px-5 flex flex-wrap gap-3 items-end bg-slate-50/80 print:bg-white">
              <label className="text-sm">
                <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">
                  Financial year
                </span>
                <select
                  className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white min-w-[6.5rem] text-sm shadow-sm"
                  value={fyStartYear}
                  onChange={(e) => setFyStartYear(Number(e.target.value))}
                  title="April–March"
                >
                  {fySelectOptions.map(({ startYear, label }) => (
                    <option key={startYear} value={startYear}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">
                  Month (FY order)
                </span>
                <select
                  className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white min-w-[10.5rem] text-sm shadow-sm"
                  value={fySlot}
                  onChange={(e) => setFySlot(Number(e.target.value))}
                  title="April through March"
                >
                  {fyMonthSequence.map((s) => (
                    <option key={`${s.calendarYear}-${s.monthIndex}`} value={s.fySlot}>
                      {MONTH_OPTIONS[s.monthIndex]} {s.calendarYear}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">Branch</span>
                <select
                  className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white min-w-[9rem] text-sm shadow-sm"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                >
                  {visibleBranchOptions.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </label>
              {isSparewayBranch ? (
                <>
                  <label className="text-sm">
                    <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">OE min</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white w-24 text-sm shadow-sm text-right tabular-nums"
                      value={config.sparewayOeMinSale ?? ""}
                      onChange={(e) => setCalcParam("sparewayOeMinSale", e.target.value)}
                    />
                  </label>
                  <label className="text-sm">
                    <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">Ord min</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white w-24 text-sm shadow-sm text-right tabular-nums"
                      value={config.sparewayOrdMinSale ?? ""}
                      onChange={(e) => setCalcParam("sparewayOrdMinSale", e.target.value)}
                    />
                  </label>
                </>
              ) : (
                <>
                  <label className="text-sm">
                    <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">OE rate %</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white w-20 text-sm shadow-sm text-right tabular-nums"
                      value={config.oeRatePct ?? ""}
                      onChange={(e) => setCalcParam("oeRatePct", e.target.value)}
                    />
                  </label>
                  <label className="text-sm">
                    <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">Ord rate %</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white w-20 text-sm shadow-sm text-right tabular-nums"
                      value={config.ordRatePct ?? ""}
                      onChange={(e) => setCalcParam("ordRatePct", e.target.value)}
                    />
                  </label>
                  <label className="text-sm">
                    <span className="block text-slate-500 mb-0.5 text-xs font-medium uppercase tracking-wide">Misc ÷</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="border border-slate-300 rounded-lg px-2.5 py-2 bg-white w-16 text-sm shadow-sm text-right tabular-nums"
                      value={config.miscDivisor ?? ""}
                      onChange={(e) => setCalcParam("miscDivisor", e.target.value)}
                      title="Blank-brand / miscellaneous ordinary split (default 8 brands)"
                    />
                  </label>
                </>
              )}
              <button
                type="button"
                className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50 ml-auto print:hidden"
                disabled={loading}
                onClick={() => reloadData()}
              >
                {loading ? "Loading…" : "Refresh data"}
              </button>
              {savedSnapshotAt ? (
                <p className="w-full text-xs text-slate-500 print:hidden">
                  Last saved to Firestore: {new Date(savedSnapshotAt).toLocaleString()}
                </p>
              ) : null}
            </div>
          </header>

          {error ? (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">{error}</div>
          ) : null}

          <section className="mb-4 rounded-xl border border-amber-200/90 bg-amber-50/50 p-4 shadow-sm print:hidden">
            <h2 className="text-sm font-semibold text-amber-950 mb-1">Customer deduction (sales_import_months)</h2>
            <p className="text-xs text-amber-900/80 mb-3 max-w-3xl">
              Pick customers from <strong>sales_import_months</strong> for <strong>{periodLabel}</strong>.
              OE and ordinary amounts on their sale lines are deducted from incentive before calculation.
            </p>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <label className="text-sm flex-1 min-w-[16rem] max-w-md">
                <span className="block text-amber-900/70 mb-0.5 text-xs">Customer name (import)</span>
                <input
                  type="text"
                  className="w-full border border-amber-300 rounded-lg px-2.5 py-2 bg-white text-sm shadow-sm"
                  list="incentive-import-customers"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder={importCustomersLoading ? "Loading customers…" : "Search customer from import…"}
                />
                <datalist id="incentive-import-customers">
                  {importCustomers.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </label>
              <button
                type="button"
                className="px-3 py-2 rounded-lg bg-amber-800 text-white text-sm hover:bg-amber-900"
                onClick={addDeductParty}
              >
                Add customer
              </button>
            </div>
            {!importCustomersLoading && importCustomersAll.length === 0 ? (
              <p className="text-xs text-amber-800/70 mb-2">
                No sale customers in sales_import_months for this month/branch. Upload sales import data first.
              </p>
            ) : null}
            {(config.deductParties || []).length ? (
              <ul className="flex flex-wrap gap-2 mb-2">
                {normalizeDeductParties(config.deductParties).map((p) => (
                  <li
                    key={p.partyName}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs text-amber-950"
                  >
                    <span>{p.partyName}</span>
                    <button
                      type="button"
                      className="text-amber-700 hover:text-red-700 font-bold leading-none"
                      onClick={() => removeDeductParty(p.partyName)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-amber-800/70">No parties selected — full report sale counts are used.</p>
            )}
            {partyDedLoading ? (
              <p className="text-xs text-amber-800">Loading party sale totals…</p>
            ) : (config.deductParties || []).length ? (
              <p className="text-xs text-amber-900 tabular-nums">
                Party deduction this month: OE <strong>{fmt(partyDeductions.totalOe)}</strong>
                {" · "}Ord. <strong>{fmt(partyDeductions.totalOrd)}</strong>
              </p>
            ) : null}
          </section>

          {isSparewayBranch && sparewaySummary ? (
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4 print:grid-cols-6">
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-4 shadow-sm">
                <div className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">
                  Total OE sale (all brands)
                  {sparewaySummary.manualBrandSales ? (
                    <span className="ml-1 normal-case text-amber-700">(manual)</span>
                  ) : null}
                </div>
                <div className="text-lg font-semibold tabular-nums text-indigo-900 mt-1">{fmt(sparewaySummary.oeSalesTotal)}</div>
              </div>
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-4 shadow-sm">
                <div className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">OE eligible</div>
                <div className="text-lg font-semibold tabular-nums text-indigo-900 mt-1">{fmt(sparewaySummary.oeEligible)}</div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 shadow-sm">
                <div className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">OE incentive @ 0.5%</div>
                <div className="text-lg font-semibold tabular-nums text-emerald-900 mt-1">{fmt(sparewaySummary.oePool)}</div>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50/70 p-4 shadow-sm">
                <div className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">
                  Total Ord. sale (all brands)
                  {sparewaySummary.manualBrandSales ? (
                    <span className="ml-1 normal-case text-amber-700">(manual)</span>
                  ) : null}
                </div>
                <div className="text-lg font-semibold tabular-nums text-violet-900 mt-1">{fmt(sparewaySummary.ordSalesTotal)}</div>
                {sparewaySummary.miscAsOrdinary > 0 ? (
                  <div className="mt-1 text-[11px] text-violet-700">
                    Includes miscellaneous: {fmt(sparewaySummary.miscAsOrdinary)}
                  </div>
                ) : null}
                {Math.round((Number(sparewaySummary.ordBlankRemainder) || 0) * 100) / 100 > 0 ? (
                  <div className="mt-1 text-[11px] text-violet-700">
                    Includes blank-brand: {fmt(sparewaySummary.ordBlankRemainder)}
                  </div>
                ) : null}
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50/70 p-4 shadow-sm">
                <div className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">Ord. eligible</div>
                <div className="text-lg font-semibold tabular-nums text-violet-900 mt-1">{fmt(sparewaySummary.ordEligible)}</div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 shadow-sm">
                <div className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">Ord. incentive @ 1%</div>
                <div className="text-lg font-semibold tabular-nums text-emerald-900 mt-1">{fmt(sparewaySummary.ordPool)}</div>
              </div>
            </div>
          ) : null}
          {isSparewayBranch && sparewaySummary ? (
            <div className="mb-5 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Total OE / Ordinary details</h3>
                  <p className="text-xs text-slate-500 mt-1 print:hidden">
                    Eligible amount = Total sale − Threshold − Branch transfer. Incentive: OE @ {SPAREWAY_OE_RATE_PCT}% · Ord. @{" "}
                    {SPAREWAY_ORD_RATE_PCT}%.
                  </p>
                </div>
                <button
                  type="button"
                  className="shrink-0 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 print:hidden"
                  onClick={saveSparewaySettingsExplicit}
                >
                  Save branch transfer
                </button>
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-right">Total sale</th>
                    <th className="px-4 py-2 text-right">Threshold</th>
                    <th className="px-4 py-2 text-right">Branch transfer</th>
                    <th className="px-4 py-2 text-right">Eligible amount</th>
                    <th className="px-4 py-2 text-right">Incentive</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium text-slate-900">OE</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmt(sparewaySummary.oeSalesTotal)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmt(sparewaySummary.oeMinSale ?? SPAREWAY_OE_MIN_SALE)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full max-w-[8rem] ml-auto border border-slate-200 rounded px-2 py-1 text-right text-sm tabular-nums print:hidden"
                        value={config.sparewayBrTransferOe ?? ""}
                        onChange={(e) => setCalcParam("sparewayBrTransferOe", e.target.value)}
                        placeholder="0"
                        title="OE eligible = Total sale − Threshold − Branch transfer"
                      />
                      <span className="hidden print:inline tabular-nums">{fmt(sparewaySummary.oeBrTransfer)}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900">
                      {fmt(sparewaySummary.oeEligible)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-800">
                      {fmt(sparewaySummary.oePool)}
                    </td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium text-slate-900">Ordinary</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmt(sparewaySummary.ordSalesTotal)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmt(sparewaySummary.ordMinSale ?? SPAREWAY_ORD_MIN_SALE)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full max-w-[8rem] ml-auto border border-slate-200 rounded px-2 py-1 text-right text-sm tabular-nums print:hidden"
                        value={config.sparewayBrTransferOrd ?? ""}
                        onChange={(e) => setCalcParam("sparewayBrTransferOrd", e.target.value)}
                        placeholder="0"
                        title="Ordinary eligible = Total sale − Threshold − Branch transfer"
                      />
                      <span className="hidden print:inline tabular-nums">{fmt(sparewaySummary.ordBrTransfer)}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900">
                      {fmt(sparewaySummary.ordEligible)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-800">
                      {fmt(sparewaySummary.ordPool)}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="px-4 py-2.5 font-semibold text-slate-900">Total</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">—</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">—</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                      {fmt((sparewaySummary.oeBrTransfer || 0) + (sparewaySummary.ordBrTransfer || 0))}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-700">
                      {fmt(sparewaySummary.oeEligible + sparewaySummary.ordEligible)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-bold text-slate-900">{fmt(grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : null}

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6 print:grid-cols-6">
            <div className="rounded-xl border border-emerald-200 bg-gradient-to-b from-emerald-50 to-emerald-100/80 p-4 shadow-sm">
              <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wide">Brand pool</div>
              <div className="text-lg font-semibold tabular-nums text-emerald-950 mt-1">{fmt(grandTotal)}</div>
              <p className="text-[11px] mt-1">
                <span className="text-violet-700 font-medium">Ord. {fmt(ordPoolTotal)}</span>
                <span className="text-slate-400 mx-1">·</span>
                <span className="text-indigo-700 font-medium">OE {fmt(oePoolTotal)}</span>
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Staff gross</div>
              <div className="text-lg font-semibold tabular-nums text-slate-900 mt-1">{fmt(metrics.totalGross)}</div>
              <p className="text-[11px] text-slate-500 mt-1">Allocated before leave</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Leave cuts</div>
              <div className="text-lg font-semibold tabular-nums text-amber-900 mt-1">{fmt(metrics.totalLeaveCuts)}</div>
              <p className="text-[11px] text-slate-500 mt-1">Redistributed in +Pool</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Late coming cut</div>
              <div className="text-lg font-semibold tabular-nums text-rose-800 mt-1">{fmt(metrics.totalCutAmount)}</div>
              <p className="text-[11px] text-slate-500 mt-1">Late coming cut amount</p>
            </div>
            <div className="rounded-xl border border-teal-200/80 bg-teal-50/60 p-4 shadow-sm ring-1 ring-teal-900/5">
              <div className="text-[11px] font-semibold text-teal-900 uppercase tracking-wide">Net pay (total)</div>
              <div className="text-lg font-semibold tabular-nums text-teal-950 mt-1">{fmt(totalFinalCombined)}</div>
              <p className="text-[11px] text-teal-800/80 mt-1">
                Staff {fmt(staffComputed.totalFinal)}
                {!isSparewayBranch && specialGroupTotal > 0 ? ` · Group ${fmt(specialGroupTotal)}` : ""}
                {staffPoolPctTotal > 0 ? ` · Pool% ${fmt(staffPoolPctTotal)}` : ""}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm col-span-2 lg:col-span-1">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Unassigned</div>
              <div className="text-lg font-semibold tabular-nums text-slate-700 mt-1">
                {fmt(staffComputed.totalUnassigned)}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Section not given a %</p>
            </div>
          </div>

        <div className="relative mb-8 print:mb-4">
            {loading ? (
              <div className="absolute inset-0 z-[2] flex items-center justify-center rounded-xl bg-white/70 backdrop-blur-[2px] print:hidden">
                <span className="text-sm font-medium text-slate-600">Loading report…</span>
              </div>
            ) : null}
            <div className="w-full overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
              {isSparewayBranch ? (
                <table className="w-full text-sm leading-tight [&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-2">
                  <thead className="sticky top-0 z-[1] shadow-sm bg-slate-100 text-left text-xs uppercase text-slate-600">
                    <tr>
                      <th className="border-b sticky left-0 z-[2] bg-slate-100">Brand</th>
                      <th className="border-b text-right bg-slate-100">Brand total</th>
                      <th className="border-b text-right bg-violet-100 text-violet-900">Ord. sale</th>
                      <th className="border-b text-right bg-indigo-100 text-indigo-900">OE sale</th>
                      <th className="border-b text-right bg-violet-200 text-violet-900">Ord. incentive</th>
                      <th className="border-b text-right bg-indigo-200 text-indigo-900">OE incentive</th>
                      <th className="border-b text-right bg-emerald-50 text-emerald-900">Total incentive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, ri) => (
                      <tr
                        key={r.brandId}
                        className={`border-b border-slate-100 hover:bg-teal-50/40 ${ri % 2 === 1 ? "bg-slate-50/40" : ""}`}
                      >
                        <td
                          className={`font-medium text-slate-900 sticky left-0 z-[1] border-r border-slate-100 ${
                            ri % 2 === 1 ? "bg-slate-50" : "bg-white"
                          }`}
                        >
                          {r.brandId}
                        </td>
                        <td className="text-right tabular-nums text-slate-700">{fmt(r.brandTotal)}</td>
                        <td className="bg-violet-50/50">
                          <input
                            type="text"
                            inputMode="decimal"
                            className={`w-full min-w-[4.5rem] border rounded px-1 py-0.5 text-right text-sm tabular-nums ${
                              r.manualOrd ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"
                            }`}
                            value={config.sparewayBrandSales?.[r.brandId]?.ord ?? ""}
                            onChange={(e) => setSparewayBrandSale(r.brandId, "ord", e.target.value)}
                            placeholder={fmt(r.computedOrd ?? r.ordSale)}
                            title={`Import: ${fmt(r.computedOrd ?? r.ordSale)}. Blank = use import.`}
                          />
                        </td>
                        <td className="bg-indigo-50/50">
                          <input
                            type="text"
                            inputMode="decimal"
                            className={`w-full min-w-[4.5rem] border rounded px-1 py-0.5 text-right text-sm tabular-nums ${
                              r.manualOe ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"
                            }`}
                            value={config.sparewayBrandSales?.[r.brandId]?.oe ?? ""}
                            onChange={(e) => setSparewayBrandSale(r.brandId, "oe", e.target.value)}
                            placeholder={fmt(r.computedOe ?? r.oeSale)}
                            title={`Import: ${fmt(r.computedOe ?? r.oeSale)}. Blank = use import.`}
                          />
                        </td>
                        <td className="text-right tabular-nums font-semibold text-violet-900 bg-violet-100/80">
                          {fmt(r.ordIncentive)}
                        </td>
                        <td className="text-right tabular-nums font-semibold text-indigo-900 bg-indigo-100/80">
                          {fmt(r.oeIncentive)}
                        </td>
                        <td className="text-right tabular-nums font-bold text-emerald-900 bg-emerald-50">{fmt(r.section)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-slate-50">
                      <td className="py-3 sticky left-0 bg-slate-50">Total</td>
                      <td className="py-3" />
                      <td className="py-3 text-right tabular-nums text-violet-800">
                        {fmt(sparewaySummary?.ordSalesTotal ?? rows.reduce((s, r) => s + (Number(r.ordSale) || 0), 0))}
                      </td>
                      <td className="py-3 text-right tabular-nums text-indigo-800">
                        {fmt(sparewaySummary?.oeSalesTotal ?? rows.reduce((s, r) => s + (Number(r.oeSale) || 0), 0))}
                      </td>
                      <td className="py-3 text-right tabular-nums text-violet-950 bg-violet-200">{fmt(ordPoolTotal)}</td>
                      <td className="py-3 text-right tabular-nums text-indigo-950 bg-indigo-200">{fmt(oePoolTotal)}</td>
                      <td className="py-3 text-right text-lg text-emerald-950 bg-emerald-100">{fmt(grandTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
              <table className="w-full table-fixed text-[11px] leading-tight [&_th]:px-1 [&_th]:py-1 [&_td]:px-1 [&_td]:py-1">
                <thead className="sticky top-0 z-[1] shadow-sm">
                  <tr className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                    <th
                      rowSpan={2}
                      className="px-2 py-2 border-b sticky left-0 z-[2] bg-slate-100 align-bottom"
                    >
                      Brand
                    </th>
                    <th
                      rowSpan={2}
                      className="px-2 py-2 border-b text-right align-bottom bg-slate-100 text-slate-700"
                      title="Current month brand total sale from import report"
                    >
                      Brand total
                    </th>
                    <th colSpan={10} className="px-2 py-1.5 border-b text-center bg-violet-100 text-violet-900 border-r-2 border-violet-300">
                      Ordinary
                    </th>
                    <th colSpan={9} className="px-2 py-1.5 border-b text-center bg-indigo-100 text-indigo-900 border-r-2 border-indigo-300">
                      OE
                    </th>
                    <th
                      rowSpan={2}
                      className="px-2 py-2 border-b text-right align-bottom bg-emerald-50 text-emerald-900"
                    >
                      Total incentive
                    </th>
                  </tr>
                  <tr className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                    <th className="px-2 py-2 border-b text-right bg-violet-100/90 text-violet-800">Ord. sale</th>
                    <th className="px-2 py-2 border-b text-right w-24 bg-violet-100/90 text-violet-800">Party ord</th>
                    <th className="px-2 py-2 border-b text-right bg-violet-100/90 text-violet-800">Net ord sale</th>
                    <th
                      className="px-2 py-2 border-b text-right bg-violet-100/90 text-violet-800"
                      title="Blank-brand / miscellaneous ordinary ÷ 8 (one share per brand)"
                    >
                      Misc ÷8
                    </th>
                    <th className="px-2 py-2 border-b text-right w-24 bg-violet-100/90 text-violet-800">Br. tr. ord</th>
                    <th className="px-2 py-2 border-b text-right bg-violet-100/90 text-violet-800">Ord. return</th>
                    <th className="px-2 py-2 border-b text-right w-24 bg-violet-100/90 text-violet-800">Min ord</th>
                    <th className="px-2 py-2 border-b text-right bg-violet-100/90 text-violet-800">Net ord</th>
                    <th
                      className="px-0.5 py-2 border-b text-right w-9 max-w-9 text-[10px] bg-violet-100/90 text-violet-800"
                      title={`Ordinary incentive rate ${ordRatePctNum}%`}
                    >
                      @{ordRatePctNum}%
                    </th>
                    <th className="px-2 py-2 border-b text-right bg-violet-200 text-violet-900 border-r-2 border-violet-300">
                      Ord. incentive
                    </th>
                    <th className="px-2 py-2 border-b text-right bg-indigo-100/90 text-indigo-800">OE sale</th>
                    <th className="px-2 py-2 border-b text-right w-24 bg-indigo-100/90 text-indigo-800">Party OE</th>
                    <th className="px-2 py-2 border-b text-right bg-indigo-100/90 text-indigo-800">Net OE sale</th>
                    <th className="px-2 py-2 border-b text-right w-24 bg-indigo-100/90 text-indigo-800">Br. transfer OE</th>
                    <th className="px-2 py-2 border-b text-right bg-indigo-100/90 text-indigo-800">OE return</th>
                    <th className="px-2 py-2 border-b text-right w-24 bg-indigo-100/90 text-indigo-800">Min OE</th>
                    <th className="px-2 py-2 border-b text-right bg-indigo-100/90 text-indigo-800">Net OE</th>
                    <th
                      className="px-0.5 py-2 border-b text-right w-9 max-w-9 text-[10px] bg-indigo-100/90 text-indigo-800"
                      title={`OE incentive rate ${oeRatePctNum}%`}
                    >
                      @{oeRatePctNum}%
                    </th>
                    <th className="px-2 py-2 border-b text-right bg-indigo-200 text-indigo-900 border-r-2 border-indigo-300">
                      OE incentive
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, ri) => (
                    <tr
                      key={r.brandId}
                      className={`border-b border-slate-100 hover:bg-teal-50/40 ${ri % 2 === 1 ? "bg-slate-50/40" : ""}`}
                    >
                      <td
                        className={`px-2 py-2 font-medium text-slate-900 sticky left-0 z-[1] border-r border-slate-100 shadow-[2px_0_0_0_rgb(248_250_252)] ${
                          ri % 2 === 1 ? "bg-slate-50" : "bg-white"
                        }`}
                      >
                        {r.brandId}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700 bg-slate-50/80">{fmt(r.brandTotal)}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-600 bg-violet-50/50">
                        {fmt(r.grossOrd ?? r.ordSale)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-amber-800 bg-violet-50/50">
                        {r.partyOrd > 0 ? fmt(-r.partyOrd) : "—"}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium bg-violet-50/50">{fmt(r.ordSale)}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-teal-800 bg-violet-50/50">
                        {r.miscShare > 0 ? fmt(r.miscShare) : "—"}
                      </td>
                      <td className="px-2 py-2 bg-violet-50/50">
                        <input
                          className="w-full min-w-0 border border-slate-200 rounded px-0.5 py-0.5 text-right text-[11px]"
                          value={config.brandCuts[r.brandId]?.ord ?? ""}
                          onChange={(e) => setCut(r.brandId, "ord", e.target.value)}
                          placeholder="0"
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700 bg-violet-50/50">{fmt(r.ordRet)}</td>
                      <td className="px-2 py-2 bg-violet-50/50">
                        <input
                          className="w-full min-w-0 border border-slate-200 rounded px-0.5 py-0.5 text-right text-[11px]"
                          value={config.mins[r.brandId]?.ordMin ?? ""}
                          onChange={(e) => setMin(r.brandId, "ordMin", e.target.value)}
                          placeholder="0"
                        />
                      </td>
                      <td
                        className="px-2 py-2 text-right tabular-nums bg-violet-50/50"
                        title={
                          r.netOrd !== r.netOrdForRate
                            ? `Before floor: ${fmt(r.netOrd)}`
                            : "Net ord sale − br transfer + Misc÷8 − return − min"
                        }
                      >
                        {fmt(r.netOrdForRate ?? r.netOrd)}
                      </td>
                      <td className="px-0.5 py-2 text-right tabular-nums text-[10px] text-violet-700 bg-violet-50/50 w-9 max-w-9">
                        {ordRatePctNum}%
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold text-violet-900 bg-violet-100 border-r-2 border-violet-300">
                        {fmt(r.ordIncentive)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-600 bg-indigo-50/50">
                        {fmt(r.grossOe ?? r.oeSale)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-amber-800 bg-indigo-50/50">
                        {r.partyOe > 0 ? fmt(-r.partyOe) : "—"}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium bg-indigo-50/50">{fmt(r.oeSale)}</td>
                      <td className="px-2 py-2 bg-indigo-50/50">
                        <input
                          className="w-full min-w-0 border border-slate-200 rounded px-0.5 py-0.5 text-right text-[11px]"
                          value={config.brandCuts[r.brandId]?.oe ?? ""}
                          onChange={(e) => setCut(r.brandId, "oe", e.target.value)}
                          placeholder="0"
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700 bg-indigo-50/50">{fmt(r.oeRet)}</td>
                      <td className="px-2 py-2 bg-indigo-50/50">
                        <input
                          className="w-full min-w-0 border border-slate-200 rounded px-0.5 py-0.5 text-right text-[11px]"
                          value={config.mins[r.brandId]?.oeMin ?? ""}
                          onChange={(e) => setMin(r.brandId, "oeMin", e.target.value)}
                          placeholder="0"
                        />
                      </td>
                      <td
                        className="px-2 py-2 text-right tabular-nums bg-indigo-50/50"
                        title={
                          r.netOe !== r.netOeForRate
                            ? `Before floor: ${fmt(r.netOe)}`
                            : "Net OE sale − br transfer − OE return − min OE"
                        }
                      >
                        {fmt(r.netOeForRate ?? r.netOe)}
                      </td>
                      <td className="px-0.5 py-2 text-right tabular-nums text-[10px] text-indigo-700 bg-indigo-50/50 w-9 max-w-9">
                        {oeRatePctNum}%
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold text-indigo-900 bg-indigo-100 border-r-2 border-indigo-300">
                        {fmt(r.oeIncentive)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-bold text-emerald-900 bg-emerald-50">{fmt(r.section)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td className="px-2 py-3 sticky left-0 bg-slate-50">Total</td>
                    <td className="px-2 py-3 bg-slate-50" />
                    <td colSpan={7} className="px-2 py-3 text-right text-violet-800 bg-violet-50">
                      Ordinary pool
                    </td>
                    <td className="px-2 py-3 bg-violet-50" />
                    <td className="px-0.5 py-3 bg-violet-50 w-9 max-w-9" />
                    <td className="px-2 py-3 text-right tabular-nums text-violet-950 bg-violet-200 border-r-2 border-violet-300">
                      {fmt(ordPoolTotal)}
                    </td>
                    <td colSpan={7} className="px-2 py-3 text-right text-indigo-800 bg-indigo-50">
                      OE pool
                    </td>
                    <td className="px-0.5 py-3 bg-indigo-50 w-9 max-w-9" />
                    <td className="px-2 py-3 text-right tabular-nums text-indigo-950 bg-indigo-200 border-r-2 border-indigo-300">
                      {fmt(oePoolTotal)}
                    </td>
                    <td className="px-2 py-3 text-right text-lg text-emerald-950 bg-emerald-100">{fmt(grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
              )}
            </div>
          </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm ring-1 ring-slate-900/[0.04] print:break-inside-avoid">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900" id="staff-allocation">
                Staff by brand
              </h2>
              <div className="flex flex-wrap items-center gap-2 print:hidden">
                <button
                  type="button"
                  className="text-sm px-3 py-1.5 rounded-lg bg-teal-700 text-white font-medium hover:bg-teal-800 shadow-sm"
                  onClick={saveStaffListExplicit}
                >
                  Save list
                </button>
                <button
                  type="button"
                  className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
                  onClick={exportStaffListJson}
                >
                  Export JSON
                </button>
                <label className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
                  Import JSON
                  <input
                    type="file"
                    accept="application/json,.json"
                    className="sr-only"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) importStaffListFromFile(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
                  onClick={() =>
                    persistConfig((prev) => ({
                      ...prev,
                      staff: [...(prev.staff || []), defaultStaffRow()],
                    }))
                  }
                >
                  Add employee line
                </button>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 leading-snug">
              Rows are saved to the cloud per branch and visible on every PC after login. Edits sync automatically;
              use <strong>Save list</strong> to force an immediate sync, or export/import to keep an offline backup.
              {cloudStatus.state === "loading" ? (
                <span className="ml-1 text-slate-400">(loading from cloud…)</span>
              ) : null}
              {cloudStatus.state === "error" ? (
                <span className="ml-1 text-amber-700">
                  (cloud sync error — using local copy: {cloudStatus.error})
                </span>
              ) : null}
              {cloudStatus.state === "ok" && cloudStatus.savedAt ? (
                <span className="ml-1 text-emerald-700">(synced)</span>
              ) : null}
            </p>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            Choose <strong>Employee</strong> from the Firestore directory for this branch (same lists as payroll /
            holidays). Each row is one person on <strong>one brand</strong>: their <strong>%</strong> applies only to
            that brand’s section total. If % for a brand sums to less than 100%, the remainder is unassigned.{" "}
            <strong>Leave cut</strong> = (gross ÷ {LEAVE_DAYS_IN_MONTH}) × leave when Auto cut is on.{" "}
            <strong>Late coming cut amount</strong> = additional less for late coming (when Late coming cut is on). These are separate — only{" "}
            <strong>Leave cut</strong> is pooled to <strong>Cash branding</strong> rows on the same brand.{" "}
            <strong>+ Pool</strong> is your share of that pot.
            Enter <strong>Leave</strong> manually for each employee and save the list.
          </p>
          {employeesError ? (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
              Could not load employees: {employeesError}
            </div>
          ) : null}
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="w-full table-fixed text-xs leading-tight [&_th]:px-1.5 [&_th]:py-1.5 [&_td]:px-1.5 [&_td]:py-1.5">
              <thead className="bg-slate-50 sticky top-0 z-[1]">
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2 pl-2 min-w-[12rem]">Employee</th>
                  <th className="py-2 pr-2 min-w-[8rem]">Brand</th>
                  <th className="py-2 pr-2 w-24 text-right">% of section</th>
                  <th className="py-2 pr-2 w-20 text-right">
                    Leave
                  </th>
                  <th className="py-2 pr-2 text-center w-[7rem]" title="Use leave days auto formula">
                    Auto cut
                  </th>
                  <th className="py-2 pr-2 text-center w-[8.5rem]" title="Enable late coming cut amount for this row">
                    Late coming cut
                  </th>
                  <th className="py-2 pr-2 w-24 text-right" title="Enter late coming cut amount (₹)">
                    Late coming ₹
                  </th>
                  <th className="py-2 pr-2 text-center w-[7.5rem]" title="Receives share of leave cuts on this brand">
                    Cash branding
                  </th>
                  <th className="py-2 pr-2 text-right">Gross</th>
                  <th className="py-2 pr-2 text-right" title="(gross ÷ days) × leave">
                    Leave cut
                  </th>
                  <th className="py-2 pr-2 text-right" title="Late coming cut amount">
                    Late coming cut amount
                  </th>
                  <th className="py-2 pr-2 text-right">+ Pool</th>
                  <th className="py-2 pr-2 text-right">Final</th>
                  <th className="py-2 w-16" />
                </tr>
              </thead>
              <tbody>
                {staffComputed.lines.map((L, ri) => {
                  const i = L.index;
                  const row = config.staff[i];
                  const selId = String(row?.employeeId || "").trim();
                  const inList = selId && employees.some((e) => e.employeeId === selId);
                  const orphan = Boolean(selId && !inList);
                  const selectValue = !selId ? "" : selId;
                  return (
                    <tr key={i} className={`border-b border-slate-100 ${ri % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                      <td className="py-2 pr-2">
                        <select
                          className="w-full min-w-[10rem] border border-slate-200 rounded px-2 py-1 bg-white text-sm"
                          value={selectValue}
                          onChange={(e) => setStaffEmployee(i, e.target.value)}
                          disabled={employeesLoading && employees.length === 0}
                          title="From Firestore Employees collection for this branch"
                        >
                          <option value="">
                            {employeesLoading ? "Loading employees…" : "Select employee…"}
                          </option>
                          {orphan ? (
                            <option value={selId}>
                              {String(row?.name || selId).trim() || selId} (not in directory)
                            </option>
                          ) : null}
                          {employees.map((emp) => (
                            <option key={emp.employeeId} value={emp.employeeId}>
                              {emp.employeeName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-2">
                        <select
                          className="w-full border border-slate-200 rounded px-2 py-1 bg-white"
                          value={config.staff[i]?.brandId ?? ""}
                          onChange={(e) => setStaffField(i, "brandId", e.target.value)}
                        >
                          <option value="">Select brand…</option>
                          {INCENTIVE_BRANDS.map((bid) => (
                            <option key={bid} value={bid}>
                              {bid}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          className="w-full border border-slate-200 rounded px-2 py-1 text-right"
                          value={config.staff[i]?.pct ?? ""}
                          onChange={(e) => setStaffField(i, "pct", e.target.value)}
                          placeholder="0"
                          inputMode="decimal"
                        />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          className="w-full border border-slate-200 rounded px-2 py-1 text-right"
                          value={config.staff[i]?.leave ?? ""}
                          onChange={(e) => setStaffField(i, "leave", e.target.value)}
                          placeholder="0"
                          title="Enter leave days manually."
                          inputMode="decimal"
                        />
                      </td>
                      <td className="py-2 pr-2 text-center align-middle">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={config.staff[i]?.autoLeaveCutEnabled !== false}
                          onChange={(e) => setStaffField(i, "autoLeaveCutEnabled", e.target.checked)}
                          title="Enable auto cut from leave days"
                        />
                      </td>
                      <td className="py-2 pr-2 text-center align-middle">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={config.staff[i]?.leaveCashCutEnabled === true}
                          onChange={(e) => setStaffField(i, "leaveCashCutEnabled", e.target.checked)}
                          title="Enable late coming cut amount for this row"
                        />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          className="w-full border border-slate-200 rounded px-2 py-1 text-right"
                          value={config.staff[i]?.leaveCashCutAmt ?? ""}
                          onChange={(e) => setStaffField(i, "leaveCashCutAmt", e.target.value)}
                          placeholder="0"
                          inputMode="decimal"
                          disabled={config.staff[i]?.leaveCashCutEnabled !== true}
                          title="Late coming cut amount (₹)"
                        />
                      </td>
                      <td className="py-2 pr-2 text-center align-middle">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={config.staff[i]?.cashBranding !== false}
                          onChange={(e) => setStaffField(i, "cashBranding", e.target.checked)}
                          title="Include in sharing leave cuts on this brand"
                        />
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums text-slate-700">{fmt(L.base)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-amber-900">{fmt(L.autoCut)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-rose-700">{fmt(L.manualCut)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-emerald-800">{fmt(L.poolShare)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums font-medium text-slate-900">{fmt(L.finalAmt)}</td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          className="text-red-600 text-xs hover:underline"
                          disabled={(config.staff || []).length <= 1}
                          onClick={() => {
                            persistConfig((prev) => {
                              const next = prev.staff.filter((_, j) => j !== i);
                              return { ...prev, staff: next.length ? next : [defaultStaffRow()] };
                            });
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200 font-semibold text-slate-800 text-sm">
                  <td colSpan={8} className="py-2 pr-2 text-right">
                    Total final (all lines)
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-slate-500">—</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-amber-900">{fmt(metrics.totalLeaveCuts)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-rose-700">{fmt(metrics.totalCutAmount)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-slate-500">—</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{fmt(staffComputed.totalFinal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {staffComputed.brandsPctOver100.length ? (
            <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1.5 mt-3">
              % totals over 100 for: <strong>{staffComputed.brandsPctOver100.join(", ")}</strong> — gross was scaled to
              fit the section; lower % on that brand.
            </p>
          ) : null}
          {staffComputed.brandsWithNoPct.length ? (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
              No lines yet for (section &gt; 0): <strong>{staffComputed.brandsWithNoPct.join(", ")}</strong> — add rows
              with that brand or leave as fully unassigned.
            </p>
          ) : null}

          {!isSparewayBranch && EMPLOYEE_SPECIAL_GROUP_INCENTIVE_PLANS.length ? (
            <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 shadow-sm">
              <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-indigo-950">Special group incentive</h3>
                  <p className="text-xs text-indigo-900/80 mt-1">
                    Product groups at <strong>1% of branch group sale</strong> (same totals as Monthly Sale Report
                    import → Group-wise). Emp. 74: AMARON, APOLO, CEAT, TVS TYRE, POWERZONE, EXIDE.
                  </p>
                </div>
                {specialGroupLoading ? (
                  <span className="text-xs text-indigo-800">Loading group sales…</span>
                ) : (
                  <span className="text-xs font-semibold text-indigo-950 tabular-nums">
                    Total {fmt(specialGroupTotal)}
                  </span>
                )}
              </div>
              <div className="overflow-x-auto rounded-lg border border-indigo-100 bg-white">
                <table className="w-full text-xs leading-tight [&_th]:px-2 [&_th]:py-1.5 [&_td]:px-2 [&_td]:py-1.5">
                  <thead className="bg-indigo-50/80 text-left uppercase text-indigo-900/70">
                    <tr>
                      <th>Employee</th>
                      <th>Group</th>
                      <th className="text-right">Sale (₹)</th>
                      <th className="text-right">Rate</th>
                      <th className="text-right">Incentive (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specialGroupLines.map((G) => (
                      <tr key={`${G.employeeId}-${G.groupId}`} className="border-t border-indigo-50">
                        <td className="font-medium text-slate-800">
                          {G.employeeName}
                          <span className="ml-1 text-slate-500">({G.employeeId})</span>
                        </td>
                        <td>{G.groupId}</td>
                        <td className="text-right tabular-nums">{fmt(G.saleAmount)}</td>
                        <td className="text-right tabular-nums">{G.ratePct}%</td>
                        <td className="text-right tabular-nums font-semibold text-indigo-950">
                          {fmt(G.incentiveAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-indigo-200 bg-indigo-50/60 font-semibold text-indigo-950">
                      <td colSpan={4} className="text-right">
                        Total group incentive
                      </td>
                      <td className="text-right tabular-nums">{fmt(specialGroupTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : null}

          <div className="mt-4 text-xs text-slate-600">
            <span className="font-medium text-slate-700">Unassigned (₹) by brand: </span>
            {rows.length === 0 ? (
              "—"
            ) : (
              <span className="tabular-nums">
                {rows
                  .map((r) => {
                    const u = staffComputed.unassignedByBrand[r.brandId] ?? 0;
                    if (u <= 0 && (Number(r.section) || 0) <= 0) return null;
                    return `${r.brandId} ${fmt(u)}`;
                  })
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </span>
            )}
            <span className="ml-2 text-slate-500">
              (Σ unassigned {fmt(staffComputed.totalUnassigned)})
            </span>
          </div>
        </div>

        {staffPoolPctOverrides.length ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 sm:p-5 shadow-sm mt-6 ring-1 ring-amber-900/[0.04] print:break-inside-avoid">
            <h2 className="text-base font-semibold text-amber-950">Staff pool % incentive</h2>
            <p className="text-xs text-amber-900/80 mt-1 mb-3">
              These employees receive a fixed % of <strong>all other staff</strong> net incentive (brand lines + special
              group). Brand % rows for them are ignored for net pay.
            </p>
            <div className="overflow-x-auto rounded-lg border border-amber-100 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-amber-900/70 border-b border-amber-100 bg-amber-50/80">
                    <th className="py-2 px-3">Employee</th>
                    <th className="py-2 px-3 text-right">Rate</th>
                    <th className="py-2 px-3 text-right">Other staff total</th>
                    <th className="py-2 px-3 text-right">Incentive</th>
                  </tr>
                </thead>
                <tbody>
                  {staffPoolPctOverrides.map((o) => (
                    <tr key={o.name} className="border-b border-amber-50">
                      <td className="py-2 px-3 font-medium text-slate-800">{o.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtPct(o.ratePct)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(o.othersTotal)}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold text-amber-950">
                        {fmt(o.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {personTotals.length ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm mt-6 ring-1 ring-slate-900/[0.04] print:break-inside-avoid">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Net pay by name</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Sums <strong>Final</strong> across all lines for the same person (multiple brands combined).{" "}
                  <strong>VINOD.MENJERI</strong> is paid <strong>5% of all other staff</strong> incentive (not brand
                  %).{" "}
                  <span className="text-slate-600">
                    WhatsApp uses the employee&apos;s <strong>Phone</strong> from the employee master when{" "}
                    <strong>Employee ID</strong> is set on staff lines; otherwise only the message text is prefilled.
                  </span>
                </p>
              </div>
              <div className="flex flex-col gap-2 print:hidden sm:min-w-[22rem]">
                <div className="flex items-end gap-2">
                  <label className="text-xs flex-1">
                    <span className="block text-slate-500 mb-0.5 font-medium uppercase tracking-wide">
                      Accountant WhatsApp no.
                    </span>
                    <input
                      value={accountantPhoneRaw}
                      onChange={(e) => setAccountantPhoneRaw(e.target.value)}
                      placeholder="e.g. 9876543210"
                      className="w-full border border-slate-300 rounded-lg px-2.5 py-2 bg-white text-sm shadow-sm"
                      inputMode="tel"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={saveAccountantPhone}
                    className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
                  >
                    Save
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={copyAccountantImage}
                    disabled={accountantImageBusy}
                    className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                  >
                    {accountantImageBusy ? "Preparing…" : "Copy image"}
                  </button>
                  <button
                    type="button"
                    onClick={downloadAccountantImage}
                    disabled={accountantImageBusy}
                    className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
                  >
                    Download image
                  </button>
                  <button
                    type="button"
                    onClick={downloadAccountantExcel}
                    className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                  >
                    Download Excel
                  </button>
                  <button
                    type="button"
                    onClick={openWhatsAppForAccountantImage}
                    disabled={accountantImageBusy}
                    title={accountantPhoneDigits ? `Send to ${accountantPhoneDigits}` : "Choose contact in WhatsApp"}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#20bd5a]"
                  >
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Send image
                  </button>
                </div>
                <p className="text-[11px] text-slate-500">
                  Image format only: open WhatsApp, then attach the downloaded image (or paste copied image).
                </p>
              </div>
            </div>
            <div className="absolute -left-[99999px] top-0 pointer-events-none opacity-0" aria-hidden>
              <div
                ref={accountantImageCardRef}
                style={{
                  width: "860px",
                  background: "#ffffff",
                  color: "#0f172a",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "24px",
                  fontFamily:
                    'Arial, Helvetica, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                <div style={{ fontSize: "22px", fontWeight: 700, marginBottom: "8px" }}>
                  Monthly Sales Incentive - Net Pay
                </div>
                <div style={{ fontSize: "13px", color: "#334155", marginBottom: "12px" }}>
                  {periodLabel} | {branchLabel} | FY {fyLabel}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto",
                    gap: "8px 12px",
                    borderTop: "1px solid #e2e8f0",
                    borderBottom: "1px solid #e2e8f0",
                    padding: "12px 0",
                  }}
                >
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>Name</div>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", textAlign: "right" }}>
                    Total %
                  </div>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", textAlign: "right" }}>
                    Net pay
                  </div>
                  {personTotalsForSave.map((row) => (
                    <React.Fragment key={`img-${row.name}`}>
                      <div style={{ fontSize: "14px" }}>{row.name}</div>
                      <div style={{ fontSize: "14px", textAlign: "right" }}>{fmtPct(row.totalPct)}</div>
                      <div style={{ fontSize: "14px", fontWeight: 600, textAlign: "right" }}>Rs {fmt(row.amount)}</div>
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ marginTop: "12px", display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                  <span style={{ fontWeight: 600 }}>Total Net Pay</span>
                  <span style={{ fontWeight: 700 }}>Rs {fmt(totalFinalCombined)}</span>
                </div>
                <div style={{ marginTop: "8px", fontSize: "11px", color: "#64748b" }}>
                  Generated on {new Date().toLocaleString("en-IN")}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto max-w-2xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 text-right" title="Sum of % of section across all brand lines">
                      Total %
                    </th>
                    <th className="py-2 text-right">Leave cut</th>
                    <th className="py-2 text-right">Late coming cut amount</th>
                    <th className="py-2 text-right">Net pay</th>
                    <th className="py-2 pl-2 text-right print:hidden">WhatsApp</th>
                  </tr>
                </thead>
                <tbody>
                  {personTotals.map(([name, amt]) => {
                    const digits = phoneDigitsForNetPayName(name);
                    const parts = personBreakdownByName.get(name) || {
                      gross: amt,
                      autoCut: 0,
                      manualCut: 0,
                      net: amt,
                      totalPct: 0,
                    };
                    const msg = buildNetPayWhatsAppText(
                      name,
                      parts.autoCut,
                      parts.manualCut,
                      parts.net,
                      parts.totalPct,
                      parts.staffPoolPct
                        ? { ratePct: parts.totalPct, othersTotal: parts.othersTotal }
                        : null
                    );
                    const href = whatsAppSendUrl(digits, msg);
                    return (
                      <tr key={name} className="border-b border-slate-100">
                        <td className="py-2 pr-4 font-medium text-slate-800">
                          {name}
                          {parts.staffPoolPct ? (
                            <span className="ml-1 text-[10px] font-normal text-amber-800">
                              ({fmtPct(parts.totalPct)} of other staff)
                            </span>
                          ) : null}
                        </td>
                        <td
                          className="py-2 text-right tabular-nums text-indigo-900"
                          title={
                            parts.staffPoolPct
                              ? `${fmtPct(parts.totalPct)} × ${fmt(parts.othersTotal)} other staff`
                              : undefined
                          }
                        >
                          {fmtPct(parts.totalPct)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-amber-900">{fmt(parts.autoCut)}</td>
                        <td className="py-2 text-right tabular-nums text-rose-700">{fmt(parts.manualCut)}</td>
                        <td className="py-2 text-right tabular-nums text-slate-900">{fmt(amt)}</td>
                        <td className="py-2 pl-2 text-right print:hidden">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => copyNetPayWhatsAppText(msg)}
                              className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                            >
                              Copy
                            </button>
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={digits ? `Send to ${digits}` : "Choose contact in WhatsApp"}
                              className="inline-flex items-center justify-center gap-1 rounded-md bg-[#25D366] px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-[#20bd5a]"
                            >
                              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                              </svg>
                              Send
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {data?.title ? (
          <p className="text-xs text-slate-500 mt-6 px-1">
            Report source: {data.title} · sales import (monthly sale report import)
          </p>
        ) : null}
        {!data && !loading ? (
          <p className="text-sm text-slate-500 mt-4 px-1">Choose financial year, month, and branch, then refresh.</p>
        ) : null}
      </div>
      </div>
    </>
  );
}
